import CloseIcon from '@mui/icons-material/Close';
import React, { useState, useEffect, useMemo, useRef } from 'react';
import { useParams, useSearchParams, useNavigate, Link } from 'react-router-dom';
import {
   Box,
   Typography,
   Paper,
   Button,
   Grid,
   CircularProgress,
   Tab,
   Tabs,
   Container,
   Alert,
   Chip,
   FormControl,
   InputLabel,
   MenuItem,
   Select,
   Card,
   CardContent,
   Avatar,
   AvatarGroup,
   Divider,
   TextField,
   IconButton,
   AlertTitle,
   FormControlLabel,
   Switch,
   CardMedia,
   Tooltip,
   List,
   ListItem,
   Dialog,
   DialogTitle,
   DialogContent,
   DialogActions,
   FormHelperText,
   Stack,
   ListSubheader,
   useMediaQuery,
   useTheme,
} from '@mui/material';
import { useResponsive, useResponsiveTypography, useResponsiveSpacing } from '../../utils/responsive';
import {
   Check as CheckIcon,
   Add as AddIcon,
   Place as PlaceIcon,
   Group as GroupIcon,
   Photo as PhotoIcon,
   Chat as ChatIcon,
   ArrowBack as ArrowBackIcon,
   ZoomIn as ZoomInIcon,
   Send as SendIcon,
   Movie as MovieIcon,
   Event as EventIcon,
   FilterList as FilterListIcon,
   CalendarMonth as CalendarMonthIcon,
   PersonOutline as PersonIcon,
   ClearAll as ClearAllIcon,
   DateRange as DateRangeIcon,
   Category as CategoryIcon,
   Settings as SettingsIcon,
   KeyboardArrowDown as KeyboardArrowDownIcon,
   BusinessCenter as BusinessCenterIcon,
   FamilyRestroom as FamilyRestroomIcon,
   People as PeopleIcon,
} from '@mui/icons-material';
import {
   collection,
   getDocs,
   query,
   orderBy,
   where,
   doc,
   getDoc,
   addDoc,
   updateDoc,
   serverTimestamp,
   limit,
   Timestamp,
} from 'firebase/firestore';
import { db } from '../../firebase/config';
import { format, isSameMonth, isWithinInterval, differenceInCalendarMonths } from 'date-fns';
import { LocalizationProvider } from '@mui/x-date-pickers';
import { DateTimePicker } from '@mui/x-date-pickers/DateTimePicker';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { SelectChangeEvent } from '@mui/material/Select';
import { EventFormData, EventType } from '../../types/Event';

// Import all from the Timeline components index
import {
   TimelineContainer,
   TimelineImageGallery,
   useTimelineData,
   useImageViewer,
   type Activity,
   type Accommodation,
   type Transportation,
   type TimelineEvent,
} from '../../components/Timeline/index';
import HistoricalTimeline from '../../components/Timeline/HistoricalTimeline';
import HistoricalTripForm from '../../components/TripForms/HistoricalTripForm';
import { useUser } from '../../contexts/UserContext';
import TripEventModal, { TripEventModalData } from '../../components/modals/TripEventModal';
import useCanAccess from '../../components/Timeline/hooks/useCanAccess';
// Import the TripGalleryGenerator component
import TripGalleryGenerator from '../../components/TripGalleryGenerator/TripGalleryGenerator';
import { TripGalleryViewer } from '../../components/TripGalleryGenerator';
// Import useEvents to access event data
import { useEvents } from '../../contexts/EventContext';
import { useSubscription } from '../../contexts/SubscriptionContext';
import { get_all_events_and_shared_events, get_all_trips_and_shared_trips } from '../../services';
import EventCreationDialog from '../../components/events/EventCreationDialog';

// Event type options
const eventTypes: { value: EventType; label: string }[] = [
   { value: 'social', label: 'Social Gathering' },
   { value: 'family', label: 'Family Event' },
   { value: 'cultural', label: 'Cultural Event' },
   { value: 'sports', label: 'Sports Event' },
   { value: 'business', label: 'Business Event' },
   { value: 'other', label: 'Other' },
];

// First, add a relationship types constant near the eventTypes constant
const relationshipTypes: { value: string; label: string; icon: JSX.Element }[] = [
   { value: 'work', label: 'Work', icon: <BusinessCenterIcon fontSize='small' /> },
   { value: 'family', label: 'Family', icon: <FamilyRestroomIcon fontSize='small' /> },
   { value: 'friends', label: 'Friends', icon: <PeopleIcon fontSize='small' /> },
   { value: 'others', label: 'Other', icon: <CategoryIcon fontSize='small' /> },
];

// First, add an inviteStatus options array near the relationshipTypes constant
const inviteStatusOptions: { value: string; label: string; color: string }[] = [
   { value: 'pending', label: 'Pending', color: 'warning.main' },
   { value: 'accepted', label: 'Accepted', color: 'success.main' },
   { value: 'declined', label: 'Declined', color: 'error.main' },
   { value: 'active', label: 'Active', color: 'info.main' },
];

// Define interfaces for our data
interface Trip {
   id: string;
   title: string;
   description?: string;
   startDate: Date;
   endDate: Date;
   destination?: string;
   coordinates?: {
      latitude?: number;
      longitude?: number;
   } | null;
   primaryDestination?: {
      name: string;
      coordinates?: [number, number];
   };
   isHistorical?: boolean;
   owner: string;
   coverPhotoUrl?: string;
   coverImageUrl?: string;
   photoCount?: number;
   collaborators?: {
      id: string;
      name: string;
      avatar?: string;
      role?: string;
   }[];
   members?: string[];
   ownerCategory?: string; // Added category for the owner
}

interface Location {
   id: string;
   name: string;
   coordinates: [number, number];
}

interface Photo {
   id: string;
   url: string;
   thumbnailUrl: string;
   title?: string;
   description?: string;
   takenAt?: Date;
   locationId?: string;
   tripId?: string;
   tags?: string[];
}

// New interface for EventItem to display on timeline
interface EventItem {
   id: string;
   title: string;
   description?: string;
   eventType: string;
   startDate: Date;
   endDate?: Date | null;
   location?: any;
   coverPhotoUrl?: string;
   participants?: Array<{ userId: string; displayName?: string; photoURL?: string }>;
}

// Add new interfaces for filters
interface TimelineFilters {
   dateRange: [Date | null, Date | null];
   entityTypes: Set<string>; // "trip", "event", "person"
   eventTypes: Set<string>; // "social", "business", etc.
   tripTypes: Set<string>; // "historical", "planned", etc.
   peopleIds: Set<string>; // User IDs to filter by
   peopleCategories: Set<string>; // Categories of people to filter by
   searchTerm: string;
}

// Add this interface for the person with category
interface PersonWithCategory {
   id: string;
   name: string;
   photoURL?: string;
   category?: string; // 'work', 'family', 'friends', etc.
   status?: string; // 'pending', 'accepted', 'declined', 'active'
}

const calculateMonthsBetween = (date1: Date, date2: Date): number => {
   return (date2.getFullYear() - date1.getFullYear()) * 12 + date2.getMonth() - date1.getMonth();
};

const TimelineView = () => {
   const { currentUser } = useUser();
   // Add the events context
   const { events: appEvents, loading: eventsLoading, createEvent } = useEvents();
   const { checkSubscriptionStatus } = useSubscription();

   // Add responsive hooks
   const theme = useTheme();
   const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
   const isTablet = useMediaQuery(theme.breakpoints.between('sm', 'md'));
   const { isMobile: responsiveIsMobile, deviceType } = useResponsive();
   const { fontSize, getFontSize } = useResponsiveTypography();
   const { spacing, getSpacing } = useResponsiveSpacing();
   const [trips, setTrips] = useState<any[]>([]);
   const [selectedTrip, setSelectedTrip] = useState<Trip | null | any>(null);
   const [activities, setActivities] = useState<Activity[]>([]);
   const [accommodations, setAccommodations] = useState<Accommodation[]>([]);
   const [transportations, setTransportations] = useState<Transportation[]>([]);
   const [photos, setPhotos] = useState<Photo[]>([]);
   const [events, setEvents] = useState<any[]>([]);
   // Add state for timeline events
   const [timelineEvents, setTimelineEvents] = useState<EventItem[]>([]);
   const [loading, setLoading] = useState(true);
   const [error, setError] = useState<string | null>(null);

   // Modal state
   const [modalData, setModalData] = useState<TripEventModalData | null>(null);
   const [modalOpen, setModalOpen] = useState(false);
   const [tabValue, setTabValue] = useState('historical');
   const [historicalFormOpen, setHistoricalFormOpen] = useState(false);
   // Add state for event creation dialog
   const [eventFormOpen, setEventFormOpen] = useState(false);
   const [photoLoading, setPhotoLoading] = useState<boolean>(false);
   const [selectedPhoto, setSelectedPhoto] = useState<Photo | null>(null);
   const [sortNewest, setSortNewest] = useState(true);
   const [missingIndexes, setMissingIndexes] = useState<string[]>([]);
   const { canUserAccess } = useCanAccess('trips', selectedTrip?.id || '');
   const [searchParams] = useSearchParams();
   const tripId = searchParams.get('trip_id') || '';
   const navigate = useNavigate();
   // Add state for available people categories
   const [availableCategories, setAvailableCategories] = useState<Set<string>>(
      new Set(['work', 'family', 'friends', 'others'])
   );
   // Add state for people dropdown
   const [peopleDropdownOpen, setPeopleDropdownOpen] = useState(false);
   // Add state for filters visibility
   const [filtersVisible, setFiltersVisible] = useState(false);

   // Add state for person filter dropdown
   const [personFilterOpen, setPersonFilterOpen] = useState(false);
   const [selectedPerson, setSelectedPerson] = useState<string | null>(null);

   // Add ref for the people filter dropdown
   const peopleFilterRef = useRef<HTMLDivElement>(null);

   const {
      imageDialogOpen,
      currentImages,
      currentImageIndex,
      openImageDialog: openImageDialogFromHook,
      closeImageDialog,
   } = useImageViewer();

   // First, add a new state to store trip thumbnails
   const [tripThumbnails, setTripThumbnails] = useState<Record<string, Photo[]>>({});

   // Add new states for filters
   const [filtersOpen, setFiltersOpen] = useState(false);
   const [filters, setFilters] = useState<TimelineFilters>({
      dateRange: [null, null],
      entityTypes: new Set(['trip', 'event']),
      eventTypes: new Set(),
      tripTypes: new Set(),
      peopleIds: new Set(),
      peopleCategories: new Set(),
      searchTerm: '',
   });
   const [availableEventTypes, setAvailableEventTypes] = useState<Set<string>>(new Set());
   const [availablePeople, setAvailablePeople] = useState<PersonWithCategory[]>([]);
   const [expandedMonths, setExpandedMonths] = useState<Set<string>>(new Set());

   // Add state for invite status filter
   const [inviteStatusFilter, setInviteStatusFilter] = useState<Set<string>>(new Set());

   // Add state for relationship data
   const [relationships, setRelationships] = useState<Array<any>>([]);
   const [isLoadingRelationships, setIsLoadingRelationships] = useState(false);

   // Add useEffect to fetch relationships when component mounts
   useEffect(() => {
      if (currentUser) {
         fetchUserRelationships();
      }
   }, [currentUser]);

   // Function to fetch user relationships
   const fetchUserRelationships = async () => {
      if (!currentUser) {
         return;
      }

      setIsLoadingRelationships(true);

      try {
         // Fetch all types of relationships
         const directRelationships: any[] = [];
         const allRelatedPeople: PersonWithCategory[] = [];

         // First, fetch the relationship_network collection
         const relationshipsRef = collection(db, 'relationship_network');
         const relationshipsQuery = query(relationshipsRef, where('userId', '==', currentUser.uid));

         const relationshipsSnapshot = await getDocs(relationshipsQuery);

         // Process relationships from the relationship_network collection
         relationshipsSnapshot.forEach((doc) => {
            const relationshipData = doc.data();
            directRelationships.push({ id: doc.id, ...relationshipData });

            // Add to related people if we have an email or userId
            if (relationshipData.relationToUserId || relationshipData.relationToEmail) {
               allRelatedPeople.push({
                  id: relationshipData.relationToUserId || doc.id,
                  name:
                     relationshipData.relationToName ||
                     relationshipData.relationToEmail ||
                     'Unknown',
                  photoURL: relationshipData.relationToPhotoURL || undefined,
                  category: relationshipData.relationType || 'other',
                  status: relationshipData.status || 'active',
               });
            }
         });

         // Next, fetch people from trip participants
         const tripParticipantsRef = collection(db, 'tripParticipants');
         const tripParticipantsQuery = query(
            tripParticipantsRef,
            where('tripUserId', '==', currentUser.uid)
         );

         const tripParticipantsSnapshot = await getDocs(tripParticipantsQuery);

         const processedUserIds = new Set<string>();
         for (const docSnapshot of tripParticipantsSnapshot.docs) {
            const participantData = docSnapshot.data();
            if (participantData.userId && participantData.userId !== currentUser.uid) {
               // Skip if we've already processed this user
               if (processedUserIds.has(participantData.userId)) continue;

               processedUserIds.add(participantData.userId);

               // Try to get user data
               try {
                  const userDocRef = doc(db, 'users', participantData.userId);
                  const userDoc = await getDoc(userDocRef);
                  if (userDoc.exists()) {
                     const userData = userDoc.data() as {
                        displayName?: string;
                        email?: string;
                        photoURL?: string;
                     };

                     // Infer category from trip metadata if possible
                     let category = 'travel';
                     if (participantData.tripId) {
                        try {
                           const tripDocRef = doc(db, 'trips', participantData.tripId);
                           const tripDoc = await getDoc(tripDocRef);
                           if (tripDoc.exists()) {
                              const tripData = tripDoc.data() as { category?: string };
                              if (tripData.category) {
                                 category = tripData.category;
                              }
                           }
                        } catch (err) {
                           console.warn('Error fetching trip for category inference:', err);
                        }
                     }

                     allRelatedPeople.push({
                        id: participantData.userId,
                        name: userData.displayName || userData.email || 'Trip Participant',
                        photoURL: userData.photoURL || undefined,
                        category,
                        status: participantData.status || 'active',
                     });
                  }
               } catch (err) {
                  console.warn('Error fetching user data for trip participant:', err);
               }
            }
         }

         // Finally, fetch people from event participants
         const eventParticipantsRef = collection(db, 'eventParticipants');
         const eventParticipantsQuery = query(
            eventParticipantsRef,
            where('userId', '==', currentUser.uid)
         );

         const eventParticipantsSnapshot = await getDocs(eventParticipantsQuery);

         for (const docSnapshot of eventParticipantsSnapshot.docs) {
            const participantData = docSnapshot.data();
            if (participantData.eventId) {
               try {
                  const eventDocRef = doc(db, 'events', participantData.eventId);
                  const eventDoc = await getDoc(eventDocRef);
                  if (eventDoc.exists()) {
                     const eventData = eventDoc.data() as {
                        participants?: Array<{
                           userId: string;
                           displayName?: string;
                           email?: string;
                           photoURL?: string;
                           status?: string;
                        }>;
                        eventType?: string;
                     };

                     // Process participants array in the event
                     const participants = eventData.participants || [];
                     if (Array.isArray(participants)) {
                        participants.forEach((participant) => {
                           if (participant.userId && participant.userId !== currentUser.uid) {
                              // Skip if we've already processed this user
                              if (processedUserIds.has(participant.userId)) return;

                              processedUserIds.add(participant.userId);

                              allRelatedPeople.push({
                                 id: participant.userId,
                                 name:
                                    participant.displayName ||
                                    participant.email ||
                                    'Event Participant',
                                 photoURL: participant.photoURL || undefined,
                                 category: eventData.eventType || 'social',
                                 status: participant.status || 'active',
                              });
                           }
                        });
                     }
                  }
               } catch (err) {
                  console.warn('Error processing event participants:', err);
               }
            }
         }

         // Update state with real data
         setRelationships(directRelationships);
         setAvailablePeople(allRelatedPeople);

         // Update available categories
         const categories = new Set<string>();
         allRelatedPeople.forEach((person) => {
            if (person.category) {
               categories.add(person.category);
            }
         });
         setAvailableCategories(categories);

         // If no people were found, log that but don't add mock data
         if (allRelatedPeople.length === 0) {
         }
      } catch (error) {
         console.error('Error fetching relationships:', error);

         // Don't add mock data on error, just show the error
         setError('Failed to load relationships. Please try refreshing the page.');
      } finally {
         setIsLoadingRelationships(false);
      }
   };

   // Add debug useEffect hook here, along with other hooks
   useEffect(() => {}, [trips, tabValue]);

   // Add useEffect to format appEvents for the timeline
   useEffect(() => {
      if (!appEvents || appEvents.length === 0) {
         setTimelineEvents([]);
         return;
      }

      // Format events for timeline display
      const formattedEvents = appEvents.map((event) => {
         // Handle timestamps correctly
         let startDate: Date;
         let endDate: Date | null = null;

         if (event.startDate) {
            // Check if object has toDate method
            if (typeof event.startDate === 'object' && 'toDate' in event.startDate) {
               startDate = (event.startDate.toDate as () => Date)();
            }
            // JavaScript Date
            else if (event.startDate instanceof Date) {
               startDate = event.startDate;
            }
            // String date
            else {
               startDate = new Date(event.startDate);
            }
         } else {
            startDate = new Date();
         }

         if (event.endDate) {
            // Firebase timestamp
            if (typeof event.endDate === 'object' && 'toDate' in event.endDate) {
               endDate = (event.endDate.toDate as () => Date)();
            }
            // JavaScript Date
            else if (event.endDate instanceof Date) {
               endDate = event.endDate;
            }
            // String date
            else {
               endDate = new Date(event.endDate);
            }
         }

         return {
            id: event.id,
            title: event.title,
            description: event.description,
            eventType: event.eventType,
            startDate,
            endDate,
            location: event.location,
            coverPhotoUrl: event.coverImageUrl,
            participants: event.participants,
         } as EventItem;
      });

      setTimelineEvents(formattedEvents);
   }, [appEvents]);

   const fetchTrips = async () => {
      try {
         // setLoading(true);
         // setError(null);
         // console.log("Starting to fetch trips...");

         if (!currentUser) {
            setTrips([]);
            setLoading(false);
            return;
         }

         const tripsData = (await get_all_trips_and_shared_trips(
            currentUser.uid,
            currentUser.email || ''
         )) as any[];

         const _tripsData: any[] = [];

         for (const trip of tripsData) {
            _tripsData.push({
               id: trip.id,
               ...trip,
               title: trip.title || 'Unnamed Trip',
               description: trip.description || '',
               startDate: new Date(trip.startDate),
               endDate: new Date(trip.endDate),
               destination: trip.destination || '',
               coordinates: trip.coordinates || null,
               primaryDestination: trip.primaryDestination || null,
               isHistorical: trip.isHistorical || false,
               owner: trip.owner || currentUser.uid,
               coverPhotoUrl: trip.coverPhotoUrl || '',
               coverImageUrl: trip.coverImageUrl || '',
               photoCount: trip.photoCount || 0,
               collaborators: trip.collaborators || [],
               members: trip.members || [],
               ownerCategory: trip.ownerCategory || inferCategory(trip.ownerName || '') || 'others',
            });
         }
         setTrips(_tripsData as any[]);
      } catch (err) {
         console.error('Error fetching trips:', err);
         setError('Failed to load trips');
      } finally {
         setLoading(false);
      }
   };

   useEffect(() => {
      if (currentUser) {
         fetchTrips();
      } else {
      }
   }, [currentUser]);

   useEffect(() => {
      if (tripId) {
         const trip = trips.find((t) => t.id === tripId);
         if (trip) {
            setSelectedTrip(trip);
            loadTripData(tripId);
            setTabValue('timeline');
         }
      }
   }, [trips, tripId]);

   const loadTripData = async (tripId: string) => {
      try {
         setLoading(true);
         setMissingIndexes([]); // Reset missing indexes array

         // Load activities with better error handling
         try {
            const activitiesQuery = query(
               collection(db, 'activities'),
               where('tripId', '==', tripId),
               orderBy('startTime')
            );
            const activitiesSnapshot = await getDocs(activitiesQuery);
            const activitiesData = activitiesSnapshot.docs.map((doc) => {
               const data = doc.data();
               return {
                  id: doc.id,
                  ...data,
                  title: data.title || '',
                  description: data.description || '',
                  date: data.date ? new Date(data.date) : new Date(),
                  location: data.location || '',
                  type: data.type || 'activity',
                  tripId: data.tripId || tripId,
                  startTime: data.startTime ? new Date(data.startTime) : null,
                  endTime: data.endTime?.toDate(),
                  coordinates: data.coordinates || null,
                  tags: data.tags || [],
               } as unknown as Activity;
            });

            // Sort manually client-side
            activitiesData.sort((a: any, b: any) => {
               // Use optional chaining with type checking
               const aTime =
                  a.startTime && typeof a.startTime.getTime === 'function'
                     ? a.startTime.getTime()
                     : 0;
               const bTime =
                  b.startTime && typeof b.startTime.getTime === 'function'
                     ? b.startTime.getTime()
                     : 0;
               return aTime - bTime;
            });

            setActivities(activitiesData);
         } catch (activityErr: any) {
            console.error('Error loading activities:', activityErr);

            // Check if it's an index error
            if (activityErr?.message?.includes('requires an index')) {
               // Extract the index URL from the error
               const indexUrlMatch = activityErr.message.match(
                  /https:\/\/console\.firebase\.google\.com[^\s]+/
               );
               const indexUrl = indexUrlMatch ? indexUrlMatch[0] : '';

               if (indexUrl) {
                  setMissingIndexes((prev) => [...prev, indexUrl]);
               }

               console.warn('Index missing for activities query. Loading without ordering...');
               // Try simplified query without orderBy as fallback
               try {
                  const simpleQuery = query(
                     collection(db, 'activities'),
                     where('tripId', '==', tripId)
                  );
                  const activitiesSnapshot = await getDocs(simpleQuery);
                  const activitiesData = activitiesSnapshot.docs.map((doc) => {
                     const data = doc.data();
                     return {
                        id: doc.id,
                        ...data,
                        title: data.title || '',
                        description: data.description || '',
                        date: data.date ? new Date(data.date) : new Date(),
                        location: data.location || '',
                        type: data.type || 'activity',
                        tripId: data.tripId || tripId,
                        startTime: data.startTime ? new Date(data.startTime) : null,
                        endTime: data.endTime?.toDate(),
                        coordinates: data.coordinates || null,
                        tags: data.tags || [],
                     } as unknown as Activity;
                  });

                  // Sort manually client-side
                  activitiesData.sort((a: any, b: any) => {
                     const aTime =
                        a.startTime && typeof a.startTime.getTime === 'function'
                           ? a.startTime.getTime()
                           : 0;
                     const bTime =
                        b.startTime && typeof b.startTime.getTime === 'function'
                           ? b.startTime.getTime()
                           : 0;
                     return aTime - bTime;
                  });

                  setActivities(activitiesData);
               } catch (fallbackErr) {
                  console.error('Fallback activities query also failed:', fallbackErr);
                  setActivities([]);
               }
            } else {
               setActivities([]);
            }
         }

         // Apply the same pattern to other queries that might have similar issues
         // Load accommodations with better error handling
         try {
            const accommodationsQuery = query(
               collection(db, 'accommodations'),
               where('tripId', '==', tripId),
               orderBy('checkIn')
            );
            const accommodationsSnapshot = await getDocs(accommodationsQuery);
            const accommodationsData = accommodationsSnapshot.docs.map((doc) => {
               const data = doc.data();
               return {
                  id: doc.id,
                  ...data,
                  name: data.name || '',
                  address: data.address || '',
                  coordinates: data.coordinates || null,
                  checkIn: data.checkIn?.toDate(),
                  checkOut: data.checkOut?.toDate(),
               } as unknown as Accommodation;
            });

            setAccommodations(accommodationsData);
         } catch (accomErr: any) {
            console.error('Error loading accommodations:', accomErr);

            // Check if it's an index error and use fallback
            if (accomErr?.message?.includes('requires an index')) {
               // Extract the index URL from the error
               const indexUrlMatch = accomErr.message.match(
                  /https:\/\/console\.firebase\.google\.com[^\s]+/
               );
               const indexUrl = indexUrlMatch ? indexUrlMatch[0] : '';

               if (indexUrl) {
                  setMissingIndexes((prev) => [...prev, indexUrl]);
               }

               console.warn('Index missing for accommodations query. Loading without ordering...');
               try {
                  const simpleQuery = query(
                     collection(db, 'accommodations'),
                     where('tripId', '==', tripId)
                  );
                  const snapshot = await getDocs(simpleQuery);
                  const data = snapshot.docs.map((doc) => {
                     const data = doc.data();
                     return {
                        id: doc.id,
                        ...data,
                        name: data.name || '',
                        address: data.address || '',
                        coordinates: data.coordinates || null,
                        checkIn: data.checkIn?.toDate(),
                        checkOut: data.checkOut?.toDate(),
                     } as unknown as Accommodation;
                  });

                  // Sort manually client-side
                  data.sort((a: any, b: any) => {
                     const aTime =
                        a.checkIn && typeof a.checkIn.getTime === 'function'
                           ? a.checkIn.getTime()
                           : 0;
                     const bTime =
                        b.checkIn && typeof b.checkIn.getTime === 'function'
                           ? b.checkIn.getTime()
                           : 0;
                     return aTime - bTime;
                  });

                  setAccommodations(data);
               } catch (fallbackErr) {
                  console.error('Fallback accommodations query also failed:', fallbackErr);
                  setAccommodations([]);
               }
            } else {
               setAccommodations([]);
            }
         }

         // Load transportations with better error handling
         try {
            const transportationsQuery = query(
               collection(db, 'transportations'),
               where('tripId', '==', tripId),
               orderBy('departureTime')
            );
            const transportationsSnapshot = await getDocs(transportationsQuery);
            const transportationsData = transportationsSnapshot.docs.map((doc) => {
               const data = doc.data();
               return {
                  id: doc.id,
                  ...data,
                  type: data.type || 'other',
                  from: data.from || '',
                  to: data.to || '',
                  departureTime: data.departureTime?.toDate(),
                  arrivalTime: data.arrivalTime?.toDate(),
               } as unknown as Transportation;
            });

            setTransportations(transportationsData);
         } catch (transportErr: any) {
            console.error('Error loading transportations:', transportErr);

            // Check if it's an index error and use fallback
            if (transportErr?.message?.includes('requires an index')) {
               // Extract the index URL from the error
               const indexUrlMatch = transportErr.message.match(
                  /https:\/\/console\.firebase\.google\.com[^\s]+/
               );
               const indexUrl = indexUrlMatch ? indexUrlMatch[0] : '';

               if (indexUrl) {
                  setMissingIndexes((prev) => [...prev, indexUrl]);
               }

               console.warn('Index missing for transportations query. Loading without ordering...');
               try {
                  const simpleQuery = query(
                     collection(db, 'transportations'),
                     where('tripId', '==', tripId)
                  );
                  const snapshot = await getDocs(simpleQuery);
                  const data = snapshot.docs.map((doc) => {
                     const data = doc.data();
                     return {
                        id: doc.id,
                        ...data,
                        type: data.type || 'other',
                        from: data.from || '',
                        to: data.to || '',
                        departureTime: data.departureTime?.toDate(),
                        arrivalTime: data.arrivalTime?.toDate(),
                     } as unknown as Transportation;
                  });

                  // Sort manually client-side
                  data.sort((a: any, b: any) => {
                     const aTime =
                        a.departureTime && typeof a.departureTime.getTime === 'function'
                           ? a.departureTime.getTime()
                           : 0;
                     const bTime =
                        b.departureTime && typeof b.departureTime.getTime === 'function'
                           ? b.departureTime.getTime()
                           : 0;
                     return aTime - bTime;
                  });

                  setTransportations(data);
               } catch (fallbackErr) {
                  console.error('Fallback transportations query also failed:', fallbackErr);
                  setTransportations([]);
               }
            } else {
               setTransportations([]);
            }
         }

         // Load photos - refactor this to properly load photos
         setPhotoLoading(true);

         try {
            const photosQuery = query(collection(db, 'photos'), where('tripId', '==', tripId));
            const photosSnapshot = await getDocs(photosQuery);
            const photosData = photosSnapshot.docs.map((doc) => {
               const data = doc.data();
               return {
                  id: doc.id,
                  ...data,
                  takenAt: data.takenAt?.toDate() || null,
                  url: data.url || '',
                  thumbnailUrl: data.thumbnailUrl || data.url || '',
                  title: data.title || '',
                  description: data.description || '',
                  locationId: data.locationId || '',
                  tripId: data.tripId || tripId,
                  tags: data.tags || [],
               } as unknown as Photo;
            });

            setPhotos(photosData);
         } catch (photoErr) {
            console.error('Error loading photos:', photoErr);
            setPhotos([]);
         } finally {
            setPhotoLoading(false);
         }

         // Load events with better error handling
         try {
            const eventsData = await get_all_events_and_shared_events(
               currentUser?.uid || '',
               currentUser?.email || ''
            );
            // const eventsQuery = query(
            //    collection(db, 'events'),
            //    where('tripId', '==', tripId),
            //    orderBy('createdAt', 'desc')
            // );
            // const eventsSnapshot = await getDocs(eventsQuery);
            // const eventsData = eventsSnapshot.docs.map((doc) => {
            //    const data = doc.data();
            //    return {
            //       id: doc.id,
            //       ...data,
            //       type: data.type || 'comment',
            //       date: data.date?.toDate() || new Date(),
            //       title: data.title || '',
            //       createdAt: data.createdAt?.toDate(),
            //       reference: data.reference || {},
            //       tripId: data.tripId || tripId,
            //    } as unknown as TimelineEvent;
            // });

            // Sort manually client-side
            eventsData.sort((a: any, b: any) => {
               // Use optional chaining with type checking
               const aTime =
                  a.createdAt && typeof a.createdAt.getTime === 'function'
                     ? a.createdAt.getTime()
                     : 0;
               const bTime =
                  b.createdAt && typeof b.createdAt.getTime === 'function'
                     ? b.createdAt.getTime()
                     : 0;
               return bTime - aTime; // Descending order
            });

            setEvents(eventsData);
         } catch (eventErr: any) {
            console.error('Error loading events:', eventErr);

            // Check if it's an index error and use fallback
            if (eventErr?.message?.includes('requires an index')) {
               // Extract the index URL from the error
               const indexUrlMatch = eventErr.message.match(
                  /https:\/\/console\.firebase\.google\.com[^\s]+/
               );
               const indexUrl = indexUrlMatch ? indexUrlMatch[0] : '';

               if (indexUrl) {
                  setMissingIndexes((prev) => [...prev, indexUrl]);
               }

               console.warn('Index missing for events query. Loading without ordering...');
               try {
                  const data = await get_all_events_and_shared_events(
                     currentUser?.uid || '',
                     currentUser?.email || ''
                  );
                  // const simpleQuery = query(
                  //    collection(db, 'events'),
                  //    where('tripId', '==', tripId)
                  // );
                  // const snapshot = await getDocs(simpleQuery);
                  // const data = snapshot.docs.map((doc) => {
                  //    const data = doc.data();
                  //    return {
                  //       id: doc.id,
                  //       ...data,
                  //       type: data.type || 'comment',
                  //       date: data.date?.toDate() || new Date(),
                  //       title: data.title || '',
                  //       createdAt: data.createdAt?.toDate(),
                  //       reference: data.reference || {},
                  //       tripId: data.tripId || tripId,
                  //    } as unknown as TimelineEvent;
                  // });

                  // Sort manually client-side (in reverse order for events)
                  data.sort((a: any, b: any) => {
                     const aTime =
                        a.createdAt && typeof a.createdAt.getTime === 'function'
                           ? a.createdAt.getTime()
                           : 0;
                     const bTime =
                        b.createdAt && typeof b.createdAt.getTime === 'function'
                           ? b.createdAt.getTime()
                           : 0;
                     return bTime - aTime; // Descending order
                  });

                  setEvents(data);
               } catch (fallbackErr) {
                  console.error('Fallback events query also failed:', fallbackErr);
                  setEvents([]);
               }
            } else {
               setEvents([]);
            }
         }
      } catch (err) {
         console.error('Error loading trip data:', err);
         setError(
            'Failed to load trip data. Please create the required Firebase indexes and try again.'
         );
      } finally {
         setLoading(false);
      }
   };

   // Handle trip selection
   const handleTripSelect = (tripId: string) => {
      const trip = trips.find((t) => t.id === tripId);
      if (trip) {
         setSelectedTrip(trip);
         loadTripData(tripId);
         setTabValue('timeline');
      }
   };

   // Handle opening trip modal
   const handleTripClick = (trip: any) => {
      const modalData: TripEventModalData = {
         id: trip.id,
         type: 'trip',
         title: trip.title,
         description: trip.description,
         startDate: trip.startDate ? new Date(trip.startDate) : undefined,
         endDate: trip.endDate ? new Date(trip.endDate) : undefined,
         destination: trip.primaryDestination?.name || trip.destination,
         location: trip.primaryDestination?.name || trip.destination,
         coverImageUrl: trip.coverPhotoUrl || trip.coverImageUrl,
         createdByName: trip.createdByName,
         participantCount: trip.participantCount,
         photoCount: trip.photoCount,
      };
      setModalData(modalData);
      setModalOpen(true);
   };

   // Handle opening event modal
   const handleEventClick = (event: any) => {
      const modalData: TripEventModalData = {
         id: event.id,
         type: 'event',
         title: event.title,
         description: event.description,
         startDate: event.startDate ? new Date(event.startDate) : undefined,
         endDate: event.endDate ? new Date(event.endDate) : undefined,
         location: event.location?.name || event.location,
         coverImageUrl: event.coverPhotoUrl,
         createdByName: event.createdByName,
         participantCount: event.participantCount,
         photoCount: event.photoCount,
      };
      setModalData(modalData);
      setModalOpen(true);
   };

   // Handle tab change
   const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
      // setTabValue(newValue);
   };

   // Handle editing an activity
   const handleEditActivity = (activityId: string) => {
      // Implement your edit logic here
   };

   // Handle deleting an activity
   const handleDeleteActivity = (activityId: string) => {
      // Implement your delete logic here
   };

   // Handle adding a comment
   const handleAddComment = (eventId: string, comment: string) => {
      // Implement your comment logic here
   };

   // After a successful trip creation, refresh the trips list
   const handleTripCreated = (tripId: string) => {
      fetchTrips();
   };

   // After a successful event creation, refresh the events list
   const handleEventCreated = (eventId: string) => {
      // The events will be refreshed via the EventContext
      // Close the event form dialog
      loadTripData(selectedTrip?.id || '');
      setEventFormOpen(false);
   };

   // Replace any custom openImageDialog with this one that uses the hook
   const openImageDialog = (images: Photo[], initialIndex: number) => {
      if (images && images.length > 0) {
         // Convert Photo[] to the format expected by the hook
         const formattedImages = images.map((photo) => ({
            id: photo.id,
            url: photo.url,
            title: photo.title || '',
            description: photo.description || '',
            date: photo.takenAt ? photo.takenAt.toISOString() : undefined,
            tags: photo.tags,
         }));
         openImageDialogFromHook(formattedImages, initialIndex);
      } else {
         console.warn('No images to display');
      }
   };

   // Function to track all available event types
   useEffect(() => {
      if (appEvents && appEvents.length > 0) {
         const types = new Set<string>();
         appEvents.forEach((event) => {
            if (event.eventType) {
               types.add(event.eventType);
            }
         });
         setAvailableEventTypes(types);
      }
   }, [appEvents]);

   // Function to collect people from trips and events with categories
   useEffect(() => {
      const people = new Map<string, PersonWithCategory>();

      // Add trip owners and collaborators
      trips.forEach((trip) => {
         if (trip.owner && trip.owner !== currentUser?.uid) {
            people.set(trip.owner, {
               id: trip.owner,
               name: trip.ownerName || 'Trip Owner',
               photoURL: trip.ownerPhotoURL,
               category: trip.ownerCategory || inferCategory(trip.ownerName || '') || 'others',
            });
         }

         if (trip.collaborators && Array.isArray(trip.collaborators)) {
            trip.collaborators.forEach((collab: any) => {
               if (collab.id && collab.id !== currentUser?.uid) {
                  people.set(collab.id, {
                     id: collab.id,
                     name: collab.name || collab.userName || 'Collaborator',
                     photoURL: collab.avatar || collab.userPhotoURL,
                     category:
                        collab.category ||
                        inferCategory(collab.name || collab.userName || '') ||
                        'others',
                  });
               }
            });
         }
      });

      // Add event participants
      appEvents.forEach((event) => {
         if (event.participants && Array.isArray(event.participants)) {
            event.participants.forEach((participant: any) => {
               if (participant.userId && participant.userId !== currentUser?.uid) {
                  const existingPerson = people.get(participant.userId);
                  const category =
                     participant.category ||
                     (event.eventType === 'business'
                        ? 'work'
                        : event.eventType === 'family'
                          ? 'family'
                          : event.eventType === 'social'
                            ? 'friends'
                            : 'others');

                  people.set(participant.userId, {
                     id: participant.userId,
                     name: participant.displayName || 'Participant',
                     photoURL: participant.photoURL,
                     category: existingPerson?.category || category,
                  });
               }
            });
         }
      });

      // Update available categories set
      const categories = new Set<string>();
      people.forEach((person) => {
         if (person.category) {
            categories.add(person.category);
         }
      });

      // Add default categories if not present
      ['work', 'family', 'friends', 'others'].forEach((cat) => categories.add(cat));

      setAvailableCategories(categories);
      setAvailablePeople(Array.from(people.values()));
   }, [trips, appEvents, currentUser]);

   // Helper function to infer category from name or context
   const inferCategory = (name: string): string | null => {
      // This is a simple example - in a real app, you might use more sophisticated logic
      name = name.toLowerCase();

      // Check for common family titles
      if (
         name.includes('mom') ||
         name.includes('dad') ||
         name.includes('mother') ||
         name.includes('father') ||
         name.includes('brother') ||
         name.includes('sister') ||
         name.includes('aunt') ||
         name.includes('uncle') ||
         name.includes('cousin') ||
         name.includes('grandma') ||
         name.includes('grandpa')
      ) {
         return 'family';
      }

      // Check for common work-related terms
      if (
         name.includes('coworker') ||
         name.includes('colleague') ||
         name.includes('boss') ||
         name.includes('manager') ||
         name.includes('employee') ||
         name.includes('client')
      ) {
         return 'work';
      }

      return null;
   };

   // Function to handle filter changes
   const handleFilterChange = (filterType: keyof TimelineFilters, value: any) => {
      setFilters((prev) => {
         const newFilters = { ...prev };

         switch (filterType) {
            case 'dateRange':
               newFilters.dateRange = value;
               break;
            case 'entityTypes':
               if (newFilters.entityTypes.has(value)) {
                  newFilters.entityTypes.delete(value);
               } else {
                  newFilters.entityTypes.add(value);
               }
               break;
            case 'eventTypes':
               if (newFilters.eventTypes.has(value)) {
                  newFilters.eventTypes.delete(value);
               } else {
                  newFilters.eventTypes.add(value);
               }
               break;
            case 'tripTypes':
               if (newFilters.tripTypes.has(value)) {
                  newFilters.tripTypes.delete(value);
               } else {
                  newFilters.tripTypes.add(value);
               }
               break;
            case 'peopleIds':
               // If we're selecting a specific person (from dropdown)
               if (typeof value === 'object' && value !== null && 'id' in value) {
                  // Clear current selection if selecting the same person
                  if (newFilters.peopleIds.has(value.id)) {
                     newFilters.peopleIds.clear();
                     setSelectedPerson(null);
                  } else {
                     // Otherwise, select only this person
                     newFilters.peopleIds.clear();
                     newFilters.peopleIds.add(value.id);
                     setSelectedPerson(value.id);
                  }
               } else {
                  // Standard toggle behavior for chip-based selection
                  if (newFilters.peopleIds.has(value)) {
                     newFilters.peopleIds.delete(value);
                     if (selectedPerson === value) {
                        setSelectedPerson(null);
                     }
                  } else {
                     newFilters.peopleIds.add(value);
                     setSelectedPerson(value);
                  }
               }
               break;
            case 'peopleCategories':
               if (newFilters.peopleCategories.has(value)) {
                  newFilters.peopleCategories.delete(value);
               } else {
                  newFilters.peopleCategories.add(value);
               }
               // When a category is selected, add all people in that category
               if (!newFilters.peopleCategories.has(value)) {
                  // We're adding a category
                  availablePeople
                     .filter((person) => person.category === value)
                     .forEach((person) => newFilters.peopleIds.add(person.id));
               } else {
                  // We're removing a category
                  // Only remove people who are exclusively in this category and not selected individually
                  availablePeople
                     .filter((person) => person.category === value)
                     .forEach((person) => {
                        // Check if this person was explicitly selected
                        const wasExplicitlySelected =
                           newFilters.peopleIds.has(person.id) &&
                           !prev.peopleCategories.has(person.category || '');
                        if (!wasExplicitlySelected) {
                           newFilters.peopleIds.delete(person.id);
                        }
                     });
               }
               break;
            case 'searchTerm':
               newFilters.searchTerm = value;
               break;
         }

         return newFilters;
      });
   };

   // Function to clear all filters
   const clearFilters = () => {
      setFilters({
         dateRange: [null, null],
         entityTypes: new Set(['trip', 'event']),
         eventTypes: new Set(),
         tripTypes: new Set(),
         peopleIds: new Set(),
         peopleCategories: new Set(),
         searchTerm: '',
      });
   };

   // Function to toggle expanded state of a month
   const toggleMonthExpansion = (monthKey: string) => {
      setExpandedMonths((prev) => {
         const newSet = new Set(prev);
         if (newSet.has(monthKey)) {
            newSet.delete(monthKey);
         } else {
            newSet.add(monthKey);
         }
         return newSet;
      });
   };

   // Filter trips based on the current filters
   const filteredTrips = useMemo(() => {
      if (!filters.entityTypes.has('trip')) {
         return [];
      }

      return trips.filter((trip) => {
         // Date range filter
         if (filters.dateRange[0] && filters.dateRange[1]) {
            const tripStart = new Date(trip.startDate);
            const tripEnd = new Date(trip.endDate);

            // Check if trip overlaps with the filter date range
            if (!(tripStart <= filters.dateRange[1]! && tripEnd >= filters.dateRange[0]!)) {
               return false;
            }
         }

         // Trip type filter
         if (filters.tripTypes.size > 0) {
            if (trip.isHistorical && !filters.tripTypes.has('historical')) {
               return false;
            }
            if (!trip.isHistorical && !filters.tripTypes.has('planned')) {
               return false;
            }
         }

         // People filter
         if (filters.peopleIds.size > 0) {
            // Check if trip owner or any collaborator is in the people filter
            let hasMatchingPerson = false;
            if (filters.peopleIds.has(trip.owner)) {
               hasMatchingPerson = true;
            } else if (trip.collaborators && Array.isArray(trip.collaborators)) {
               for (const collab of trip.collaborators) {
                  if (filters.peopleIds.has(collab.id)) {
                     hasMatchingPerson = true;
                     break;
                  }
               }
            }

            if (!hasMatchingPerson) {
               return false;
            }
         }

         // Search term filter
         if (filters.searchTerm) {
            const searchLower = filters.searchTerm.toLowerCase();
            const titleMatch = trip.title?.toLowerCase().includes(searchLower);
            const descMatch = trip.description?.toLowerCase().includes(searchLower);
            const destMatch =
               trip.destination?.toLowerCase().includes(searchLower) ||
               trip.primaryDestination?.name?.toLowerCase().includes(searchLower);

            if (!(titleMatch || descMatch || destMatch)) {
               return false;
            }
         }

         return true;
      });
   }, [trips, filters]);

   // Filter events based on the current filters
   const filteredEvents = useMemo(() => {
      if (!filters.entityTypes.has('event')) {
         return [];
      }

      return timelineEvents.filter((event) => {
         // Date range filter
         if (filters.dateRange[0] && filters.dateRange[1]) {
            const eventStart = new Date(event.startDate);
            const eventEnd = event.endDate ? new Date(event.endDate) : eventStart;

            // Check if event overlaps with filter date range
            if (!(eventStart <= filters.dateRange[1]! && eventEnd >= filters.dateRange[0]!)) {
               return false;
            }
         }

         // Event type filter
         if (filters.eventTypes.size > 0 && !filters.eventTypes.has(event.eventType)) {
            return false;
         }

         // People filter (if we have participant data)
         if (filters.peopleIds.size > 0 && event.participants) {
            let hasMatchingPerson = false;
            for (const participant of event.participants) {
               if (filters.peopleIds.has(participant.userId)) {
                  hasMatchingPerson = true;
                  break;
               }
            }

            if (!hasMatchingPerson) {
               return false;
            }
         }

         // Search term filter
         if (filters.searchTerm) {
            const searchLower = filters.searchTerm.toLowerCase();
            const titleMatch = event.title?.toLowerCase().includes(searchLower);
            const descMatch = event.description?.toLowerCase().includes(searchLower);
            const locationMatch =
               event.location?.name?.toLowerCase().includes(searchLower) ||
               event.location?.address?.toLowerCase().includes(searchLower);

            if (!(titleMatch || descMatch || locationMatch)) {
               return false;
            }
         }

         return true;
      });
   }, [timelineEvents, filters]);

   // Now, let's update the renderYearlyTimeline function to group by months and support filters
   const renderYearlyTimeline = () => {
      // Use filtered items
      const filteredTripsByYear: Record<number, Trip[]> = {};
      filteredTrips.forEach((trip) => {
         const year = new Date(trip.startDate).getFullYear();
         if (!filteredTripsByYear[year]) {
            filteredTripsByYear[year] = [];
         }
         filteredTripsByYear[year].push(trip);
      });

      // Group events by year
      const filteredEventsByYear: Record<number, EventItem[]> = {};
      filteredEvents.forEach((event) => {
         const year = new Date(event.startDate).getFullYear();
         if (!filteredEventsByYear[year]) {
            filteredEventsByYear[year] = [];
         }
         filteredEventsByYear[year].push(event);
      });

      // Group by months within years
      const tripsByYearAndMonth: Record<string, Trip[]> = {};
      filteredTrips.forEach((trip) => {
         const date = new Date(trip.startDate);
         const year = date.getFullYear();
         const month = date.getMonth();
         const key = `${year}-${month}`;

         if (!tripsByYearAndMonth[key]) {
            tripsByYearAndMonth[key] = [];
         }
         tripsByYearAndMonth[key].push(trip);
      });

      const eventsByYearAndMonth: Record<string, EventItem[]> = {};
      filteredEvents.forEach((event) => {
         const date = new Date(event.startDate);
         const year = date.getFullYear();
         const month = date.getMonth();
         const key = `${year}-${month}`;

         if (!eventsByYearAndMonth[key]) {
            eventsByYearAndMonth[key] = [];
         }
         eventsByYearAndMonth[key].push(event);
      });

      // Get all unique years from both filtered trips and events
      const allYears = new Set<number>();
      Object.keys(filteredTripsByYear).forEach((year) => allYears.add(Number(year)));
      Object.keys(filteredEventsByYear).forEach((year) => allYears.add(Number(year)));

      // Convert to array and sort
      const years = Array.from(allYears).sort((a, b) => (sortNewest ? b - a : a - b));

      // Format a date for display
      const formatDate = (date: Date) => {
         return date.toLocaleDateString('en-US', {
            month: 'short',
            day: 'numeric',
            year: 'numeric',
         });
      };

      // Get all unique month keys across trips and events
      const allMonthKeys = new Set<string>();
      Object.keys(tripsByYearAndMonth).forEach((key) => allMonthKeys.add(key));
      Object.keys(eventsByYearAndMonth).forEach((key) => allMonthKeys.add(key));

      // No data to display message
      if (years.length === 0) {
         return (
            <Box sx={{ textAlign: 'center', py: 8 }}>
               <Typography variant='h6' color='text.secondary'>
                  No data matches your filters
               </Typography>
               <Button
                  variant='outlined'
                  startIcon={<ClearAllIcon />}
                  onClick={clearFilters}
                  sx={{ mt: 2 }}
               >
                  Clear All Filters
               </Button>
            </Box>
         );
      }

      return (
         <>
            {years.map((year, yearIndex) => (
               <React.Fragment key={year}>
                  {/* Year marker */}
                  <Box
                     sx={{
                        position: 'relative',
                        mb: 3,
                        mt: yearIndex === 0 ? 0 : 4,
                        ml: isMobile ? 0 : -8,
                        display: 'flex',
                        justifyContent: isMobile ? 'center' : 'flex-start',
                     }}
                  >
                     <Box
                        sx={{
                           position: 'sticky',
                           top: 80,
                           display: 'flex',
                           alignItems: 'center',
                           zIndex: 2,
                        }}
                     >
                        <Typography
                           variant='h5'
                           sx={{
                              fontWeight: 'bold',
                              p: isMobile ? 1.5 : 2,
                              bgcolor: 'background.paper',
                              borderRadius: 2,
                              boxShadow: 1,
                              fontSize: isMobile ? fontSize.h6 : 'inherit',
                           }}
                        >
                           {year}
                        </Typography>
                     </Box>
                  </Box>

                  {/* Months for this year */}
                  {Array.from({ length: 12 }).map((_, monthIndex) => {
                     const monthKey = `${year}-${monthIndex}`;
                     const monthHasTrips = !!tripsByYearAndMonth[monthKey]?.length;
                     const monthHasEvents = !!eventsByYearAndMonth[monthKey]?.length;

                     // Skip months with no data
                     if (!monthHasTrips && !monthHasEvents) return null;

                     const monthDate = new Date(year, monthIndex, 1);
                     const monthName = format(monthDate, 'MMMM');
                     const isExpanded = expandedMonths.has(monthKey);

                     return (
                        <Box key={monthKey} sx={{ mb: 4 }}>
                           {/* Month Header */}
                           <Paper
                              sx={{
                                 p: isMobile ? 1.5 : 2,
                                 mb: 2,
                                 mx: isMobile ? 0.5 : 0,
                                 display: 'flex',
                                 justifyContent: 'space-between',
                                 alignItems: 'center',
                                 boxShadow: 2,
                                 borderLeft: 4,
                                 borderColor: 'primary.main',
                                 cursor: 'pointer',
                                 flexDirection: isMobile ? 'column' : 'row',
                                 gap: isMobile ? 1 : 0,
                              }}
                              onClick={() => toggleMonthExpansion(monthKey)}
                           >
                              <Box sx={{ display: 'flex', alignItems: 'center', width: isMobile ? '100%' : 'auto' }}>
                                 <CalendarMonthIcon sx={{ mr: 1, color: 'text.secondary' }} />
                                 <Typography variant='h6' sx={{ fontSize: isMobile ? fontSize.subtitle1 : 'inherit' }}>{monthName}</Typography>
                              </Box>
                              <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 0.5, justifyContent: isMobile ? 'center' : 'flex-end', width: isMobile ? '100%' : 'auto' }}>
                                 {monthHasTrips && (
                                    <Chip
                                       size='small'
                                       label={`${tripsByYearAndMonth[monthKey].length} Trips`}
                                       color='primary'
                                       variant='outlined'
                                       sx={{ mr: 1 }}
                                    />
                                 )}
                                 {monthHasEvents && (
                                    <Chip
                                       size='small'
                                       label={`${eventsByYearAndMonth[monthKey].length} Events`}
                                       color='secondary'
                                       variant='outlined'
                                       sx={{ mr: 1 }}
                                    />
                                 )}
                                 <KeyboardArrowDownIcon
                                    sx={{
                                       transform: isExpanded ? 'rotate(180deg)' : 'rotate(0deg)',
                                       transition: 'transform 0.3s',
                                    }}
                                 />
                              </Box>
                           </Paper>

                           {/* Month Content (Collapsible) */}
                           {isExpanded && (
                              <Box>
                                 {/* Trips for this month */}
                                 {monthHasTrips && (
                                    <Box sx={{ mb: 4, mx: isMobile ? 0.5 : 0 }}>
                                       <Typography
                                          variant='h6'
                                          sx={{
                                             mb: 2,
                                             display: 'flex',
                                             alignItems: 'center',
                                             color: 'primary.main',
                                             fontSize: isMobile ? fontSize.subtitle1 : 'inherit',
                                          }}
                                       >
                                          <PlaceIcon sx={{ mr: 1 }} /> Trips
                                       </Typography>
                                       <Grid container spacing={isMobile ? 2 : 3}>
                                          {tripsByYearAndMonth[monthKey].map((trip) => (
                                             <Grid item xs={12} sm={6} md={4} key={trip.id}>
                                                <Card
                                                   sx={{
                                                      cursor: 'pointer',
                                                      height: '100%',
                                                      display: 'flex',
                                                      flexDirection: 'column',
                                                      '&:hover': {
                                                         boxShadow: 6,
                                                         transform: 'translateY(-4px)',
                                                         transition: 'transform 0.3s ease-in-out',
                                                      },
                                                   }}
                                                   onClick={() => handleTripClick(trip)}
                                                >
                                                   <CardMedia
                                                      component='img'
                                                      height='140'
                                                      image={
                                                         trip.coverPhotoUrl ||
                                                         trip.coverImageUrl ||
                                                         '/placeholder-trip.jpg'
                                                      }
                                                      alt={trip.title}
                                                   />
                                                   <CardContent sx={{ flexGrow: 1 }}>
                                                      <Typography
                                                         variant='h6'
                                                         component='div'
                                                         gutterBottom
                                                      >
                                                         {trip.title}
                                                      </Typography>
                                                      <Box
                                                         sx={{
                                                            mb: 1,
                                                            display: 'flex',
                                                            alignItems: 'center',
                                                         }}
                                                      >
                                                         <Chip
                                                            label={
                                                               trip.isHistorical
                                                                  ? 'Historical'
                                                                  : 'Planned'
                                                            }
                                                            size='small'
                                                            color={
                                                               trip.isHistorical
                                                                  ? 'secondary'
                                                                  : 'primary'
                                                            }
                                                            sx={{ mr: 1 }}
                                                         />
                                                         {trip.destination && (
                                                            <Tooltip title={trip.destination}>
                                                               <PlaceIcon
                                                                  fontSize='small'
                                                                  color='action'
                                                               />
                                                            </Tooltip>
                                                         )}
                                                      </Box>
                                                      <Typography
                                                         variant='body2'
                                                         color='text.secondary'
                                                         gutterBottom
                                                      >
                                                         {formatDate(trip.startDate)}
                                                         {trip.endDate &&
                                                            ` - ${formatDate(trip.endDate)}`}
                                                      </Typography>
                                                      <Typography
                                                         variant='body2'
                                                         color='text.secondary'
                                                         sx={{
                                                            display: '-webkit-box',
                                                            WebkitLineClamp: 2,
                                                            WebkitBoxOrient: 'vertical',
                                                            overflow: 'hidden',
                                                            textOverflow: 'ellipsis',
                                                         }}
                                                      >
                                                         {trip.description}
                                                      </Typography>
                                                   </CardContent>
                                                </Card>
                                             </Grid>
                                          ))}
                                       </Grid>
                                    </Box>
                                 )}

                                 {/* Events for this month */}
                                 {monthHasEvents && (
                                    <Box sx={{ mb: 4, mx: isMobile ? 0.5 : 0 }}>
                                       <Typography
                                          variant='h6'
                                          sx={{
                                             mb: 2,
                                             display: 'flex',
                                             alignItems: 'center',
                                             color: 'secondary.main',
                                             fontSize: isMobile ? fontSize.subtitle1 : 'inherit',
                                          }}
                                       >
                                          <EventIcon sx={{ mr: 1 }} /> Events
                                       </Typography>
                                       <Grid container spacing={isMobile ? 2 : 3}>
                                          {eventsByYearAndMonth[monthKey].map((event) => (
                                             <Grid item xs={12} sm={6} md={4} key={event.id}>
                                                <Card
                                                   sx={{
                                                      cursor: 'pointer',
                                                      height: '100%',
                                                      display: 'flex',
                                                      flexDirection: 'column',
                                                      '&:hover': {
                                                         boxShadow: 6,
                                                         transform: 'translateY(-4px)',
                                                         transition: 'transform 0.3s ease-in-out',
                                                      },
                                                   }}
                                                   onClick={() => handleEventClick(event)}
                                                >
                                                   <CardMedia
                                                      component='img'
                                                      height='140'
                                                      image={
                                                         event.coverPhotoUrl ||
                                                         '/placeholder-event.jpg'
                                                      }
                                                      alt={event.title}
                                                   />
                                                   <CardContent sx={{ flexGrow: 1 }}>
                                                      <Typography
                                                         variant='h6'
                                                         component='div'
                                                         gutterBottom
                                                      >
                                                         {event.title}
                                                      </Typography>
                                                      <Box
                                                         sx={{
                                                            mb: 1,
                                                            display: 'flex',
                                                            alignItems: 'center',
                                                         }}
                                                      >
                                                         <Chip
                                                            label={event.eventType}
                                                            size='small'
                                                            color='secondary'
                                                            sx={{ mr: 1 }}
                                                         />
                                                         {event.location && (
                                                            <Tooltip
                                                               title={
                                                                  event.location.name || 'Location'
                                                               }
                                                            >
                                                               <PlaceIcon
                                                                  fontSize='small'
                                                                  color='action'
                                                               />
                                                            </Tooltip>
                                                         )}
                                                      </Box>
                                                      <Typography
                                                         variant='body2'
                                                         color='text.secondary'
                                                         gutterBottom
                                                      >
                                                         {formatDate(event.startDate)}
                                                         {event.endDate &&
                                                            ` - ${formatDate(event.endDate)}`}
                                                      </Typography>
                                                      <Typography
                                                         variant='body2'
                                                         color='text.secondary'
                                                         sx={{
                                                            display: '-webkit-box',
                                                            WebkitLineClamp: 2,
                                                            WebkitBoxOrient: 'vertical',
                                                            overflow: 'hidden',
                                                            textOverflow: 'ellipsis',
                                                         }}
                                                      >
                                                         {event.description}
                                                      </Typography>
                                                   </CardContent>
                                                </Card>
                                             </Grid>
                                          ))}
                                       </Grid>
                                    </Box>
                                 )}
                              </Box>
                           )}
                        </Box>
                     );
                  })}
               </React.Fragment>
            ))}
         </>
      );
   };

   // Update the renderFilters function to be more compact
   const renderFilters = () => {
      return (
         <Paper sx={{ p: 2, mb: 3, mx: isMobile ? 1 : 0, borderRadius: 2, boxShadow: 2 }}>
            <Box
               sx={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  mb: 2,
                  flexDirection: isMobile ? 'column' : 'row',
                  gap: isMobile ? 1 : 0,
               }}
            >
               <Typography
                  variant='subtitle1'
                  sx={{ display: 'flex', alignItems: 'center', fontWeight: 'medium' }}
               >
                  <FilterListIcon sx={{ mr: 1 }} /> Filter Your Timeline
               </Typography>
               <Button
                  size='small'
                  startIcon={<ClearAllIcon />}
                  onClick={clearFilters}
                  variant='outlined'
               >
                  Clear All
               </Button>
            </Box>

            <Divider sx={{ mb: 2 }} />

            <Grid container spacing={2}>
               {/* Left column - Date Range and Search */}
               <Grid item xs={12} md={4}>
                  <TextField
                     fullWidth
                     size='small'
                     label='Search timeline'
                     value={filters.searchTerm}
                     onChange={(e) => handleFilterChange('searchTerm', e.target.value)}
                     placeholder='Search by title, description, location...'
                     sx={{ mb: 2 }}
                  />

                  <Typography
                     variant='subtitle2'
                     gutterBottom
                     sx={{ display: 'flex', alignItems: 'center' }}
                  >
                     <DateRangeIcon fontSize='small' sx={{ mr: 0.5 }} /> Date Range
                  </Typography>

                  <LocalizationProvider dateAdapter={AdapterDateFns}>
                     <DateTimePicker
                        label='From'
                        value={filters.dateRange[0]}
                        onChange={(date) => {
                           const newRange: [Date | null, Date | null] = [...filters.dateRange];
                           newRange[0] = date as Date | null;
                           handleFilterChange('dateRange', newRange);
                        }}
                        renderInput={(params) => (
                           <TextField {...params} size='small' fullWidth sx={{ mb: 1 }} />
                        )}
                     />
                  </LocalizationProvider>

                  <LocalizationProvider dateAdapter={AdapterDateFns}>
                     <DateTimePicker
                        label='To'
                        value={filters.dateRange[1]}
                        onChange={(date) => {
                           const newRange: [Date | null, Date | null] = [...filters.dateRange];
                           newRange[1] = date as Date | null;
                           handleFilterChange('dateRange', newRange);
                        }}
                        renderInput={(params: any) => (
                           <TextField {...params} size='small' fullWidth />
                        )}
                     />
                  </LocalizationProvider>
               </Grid>

               {/* Middle column - Event & Trip Types */}
               <Grid item xs={12} md={4}>
                  <Typography
                     variant='subtitle2'
                     gutterBottom
                     sx={{ display: 'flex', alignItems: 'center' }}
                  >
                     <CategoryIcon fontSize='small' sx={{ mr: 0.5 }} /> Show
                  </Typography>
                  <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mb: 2 }}>
                     <Chip
                        label='Trips'
                        color={filters.entityTypes.has('trip') ? 'primary' : 'default'}
                        onClick={() => handleFilterChange('entityTypes', 'trip')}
                        clickable
                     />
                     <Chip
                        label='Events'
                        color={filters.entityTypes.has('event') ? 'secondary' : 'default'}
                        onClick={() => handleFilterChange('entityTypes', 'event')}
                        clickable
                     />
                  </Box>

                  {filters.entityTypes.has('event') && availableEventTypes.size > 0 && (
                     <Box sx={{ mb: 2 }}>
                        <Typography
                           variant='subtitle2'
                           gutterBottom
                           sx={{ display: 'flex', alignItems: 'center' }}
                        >
                           <EventIcon fontSize='small' sx={{ mr: 0.5 }} /> Event Types
                        </Typography>
                        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                           {Array.from(availableEventTypes).map((type) => (
                              <Chip
                                 key={type}
                                 label={type}
                                 color={filters.eventTypes.has(type) ? 'secondary' : 'default'}
                                 onClick={() => handleFilterChange('eventTypes', type)}
                                 clickable
                                 size='small'
                              />
                           ))}
                        </Box>
                     </Box>
                  )}

                  {filters.entityTypes.has('trip') && (
                     <Box sx={{ mb: 2 }}>
                        <Typography
                           variant='subtitle2'
                           gutterBottom
                           sx={{ display: 'flex', alignItems: 'center' }}
                        >
                           <PlaceIcon fontSize='small' sx={{ mr: 0.5 }} /> Trip Types
                        </Typography>
                        <Box sx={{ display: 'flex', gap: 1 }}>
                           <Chip
                              label='Historical'
                              color={filters.tripTypes.has('historical') ? 'primary' : 'default'}
                              onClick={() => handleFilterChange('tripTypes', 'historical')}
                              clickable
                              size='small'
                           />
                           <Chip
                              label='Planned'
                              color={filters.tripTypes.has('planned') ? 'primary' : 'default'}
                              onClick={() => handleFilterChange('tripTypes', 'planned')}
                              clickable
                              size='small'
                           />
                        </Box>
                     </Box>
                  )}
               </Grid>

               {/* Right column - People Filter Preview */}
               <Grid item xs={12} md={4}>
                  <Typography
                     variant='subtitle2'
                     gutterBottom
                     sx={{ display: 'flex', alignItems: 'center' }}
                  >
                     <PersonIcon fontSize='small' sx={{ mr: 0.5 }} /> People Quick Filter
                  </Typography>

                  {/* Show selected people count and a button to open people filter section */}
                  <Box
                     sx={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        mb: 1,
                     }}
                  >
                     <Typography variant='body2' color='text.secondary'>
                        {isLoadingRelationships ? (
                           <Box sx={{ display: 'flex', alignItems: 'center' }}>
                              <CircularProgress size={14} sx={{ mr: 1 }} /> Loading relationships...
                           </Box>
                        ) : filters.peopleIds.size > 0 ? (
                           `${filters.peopleIds.size} ${filters.peopleIds.size === 1 ? 'person' : 'people'} selected`
                        ) : (
                           `${availablePeople.length} people available`
                        )}
                     </Typography>

                     <Button
                        size='small'
                        variant='contained'
                        color='primary'
                        onClick={() => {
                           // Force set to true instead of toggling
                           setPeopleDropdownOpen(true);

                           // Scroll to the people section with a delay to ensure rendering
                           setTimeout(() => {
                              const peopleSection =
                                 document.getElementById('people-filter-section');
                              if (peopleSection) {
                                 peopleSection.scrollIntoView({ behavior: 'smooth' });
                              }
                           }, 200);
                        }}
                        endIcon={<KeyboardArrowDownIcon />}
                     >
                        Manage People
                     </Button>
                  </Box>

                  {/* Show selected people as chips */}
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                     {Array.from(filters.peopleIds)
                        .slice(0, 3)
                        .map((personId) => {
                           const person = availablePeople.find((p) => p.id === personId);
                           if (!person) return null;

                           return (
                              <Chip
                                 key={person.id}
                                 label={person.name}
                                 size='small'
                                 color='primary'
                                 onDelete={() => handleFilterChange('peopleIds', person.id)}
                              />
                           );
                        })}

                     {filters.peopleIds.size > 3 && (
                        <Chip
                           label={`+${filters.peopleIds.size - 3} more`}
                           size='small'
                           variant='outlined'
                           onClick={() => setPeopleDropdownOpen(true)}
                        />
                     )}
                  </Box>
               </Grid>
            </Grid>

            {/* People & Relationships Filter Section */}
            {
               <Box
                  sx={{
                     mt: 3,
                     mb: 3,
                     p: 3,
                     border: '1px solid',
                     borderColor: 'primary.main',
                     borderRadius: 2,
                     bgcolor: 'background.paper',
                  }}
                  id='people-filter-section'
               >
                  <Box
                     sx={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        mb: 2,
                     }}
                  >
                     <Typography
                        variant='h6'
                        sx={{
                           display: 'flex',
                           alignItems: 'center',
                           fontWeight: 'bold',
                           color: 'primary.main',
                        }}
                     >
                        <PersonIcon sx={{ mr: 1 }} /> People & Relationships Filter
                     </Typography>

                     <Button
                        size='small'
                        variant='outlined'
                        color='primary'
                        onClick={() => setPeopleDropdownOpen(!peopleDropdownOpen)}
                        endIcon={
                           <KeyboardArrowDownIcon
                              sx={{
                                 transform: peopleDropdownOpen ? 'rotate(180deg)' : 'none',
                                 transition: 'transform 0.3s',
                              }}
                           />
                        }
                     >
                        {peopleDropdownOpen ? 'Hide People' : 'Show People'}
                     </Button>
                  </Box>

                  {isLoadingRelationships ? (
                     <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
                        <CircularProgress size={30} />
                     </Box>
                  ) : availablePeople.length === 0 ? (
                     <Alert severity='info' sx={{ mb: 2 }}>
                        No people data available. Try adding trips or events with participants
                        first.
                     </Alert>
                  ) : (
                     <>
                        {/* Always show relationship types and invite status filters */}
                        <Grid container spacing={2}>
                           {/* Relationship Type Filter */}
                           <Grid item xs={12} md={6}>
                              <Typography variant='subtitle2' gutterBottom>
                                 Filter by Relationship Type
                              </Typography>
                              <Stack
                                 direction='row'
                                 spacing={1}
                                 flexWrap='wrap'
                                 useFlexGap
                                 sx={{ mb: 2 }}
                              >
                                 {relationshipTypes.map((type) => (
                                    <Chip
                                       key={type.value}
                                       label={`${type.label} (${availablePeople.filter((p) => p.category === type.value).length})`}
                                       icon={type.icon}
                                       color={
                                          filters.peopleCategories.has(type.value)
                                             ? 'primary'
                                             : 'default'
                                       }
                                       onClick={() =>
                                          handleFilterChange('peopleCategories', type.value)
                                       }
                                       sx={{ mb: 1 }}
                                       disabled={
                                          availablePeople.filter((p) => p.category === type.value)
                                             .length === 0
                                       }
                                    />
                                 ))}
                              </Stack>

                              {/* Invite Status Filter */}
                              <Typography variant='subtitle2' gutterBottom sx={{ mt: 2 }}>
                                 Filter by Invite Status
                              </Typography>
                              <Stack
                                 direction='row'
                                 spacing={1}
                                 flexWrap='wrap'
                                 useFlexGap
                                 sx={{ mb: 2 }}
                              >
                                 {inviteStatusOptions.map((status) => (
                                    <Chip
                                       key={status.value}
                                       label={`${status.label} (${availablePeople.filter((p) => p.status === status.value).length})`}
                                       color={
                                          inviteStatusFilter.has(status.value)
                                             ? 'primary'
                                             : 'default'
                                       }
                                       onClick={() => {
                                          setInviteStatusFilter((prev) => {
                                             const newSet = new Set(prev);
                                             if (newSet.has(status.value)) {
                                                newSet.delete(status.value);
                                             } else {
                                                newSet.add(status.value);
                                             }
                                             return newSet;
                                          });
                                       }}
                                       sx={{
                                          mb: 1,
                                          borderColor: inviteStatusFilter.has(status.value)
                                             ? 'primary.main'
                                             : status.color,
                                          color: inviteStatusFilter.has(status.value)
                                             ? 'primary.main'
                                             : status.color,
                                          '&.MuiChip-outlined': {
                                             backgroundColor: inviteStatusFilter.has(status.value)
                                                ? 'primary.50'
                                                : 'transparent',
                                          },
                                       }}
                                       variant='outlined'
                                       disabled={
                                          availablePeople.filter((p) => p.status === status.value)
                                             .length === 0
                                       }
                                    />
                                 ))}
                              </Stack>
                           </Grid>

                           {/* People Dropdown Filter */}
                           <Grid item xs={12} md={6}>
                              <Typography variant='subtitle2' gutterBottom>
                                 Filter by Specific People
                              </Typography>

                              {/* Available people count */}
                              <Typography variant='body2' color='text.secondary' gutterBottom>
                                 {availablePeople.length} people available for filtering
                              </Typography>

                              {/* Selected people chips */}
                              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mb: 1 }}>
                                 {Array.from(filters.peopleIds).map((personId) => {
                                    const person = availablePeople.find((p) => p.id === personId);
                                    if (!person) return null;

                                    return (
                                       <Chip
                                          key={person.id}
                                          label={person.name}
                                          color='primary'
                                          onDelete={() =>
                                             handleFilterChange('peopleIds', person.id)
                                          }
                                          size='small'
                                          avatar={
                                             person.photoURL ? (
                                                <Avatar src={person.photoURL} />
                                             ) : undefined
                                          }
                                       />
                                    );
                                 })}

                                 {filters.peopleIds.size === 0 && (
                                    <Typography variant='body2' color='text.secondary'>
                                       No people selected
                                    </Typography>
                                 )}
                              </Box>
                           </Grid>
                        </Grid>

                        {/* People selection dropdown */}
                        {peopleDropdownOpen && (
                           <Box
                              sx={{
                                 mt: 2,
                                 p: 2,
                                 border: '1px solid rgba(0,0,0,0.1)',
                                 borderRadius: 1,
                                 bgcolor: 'background.paper',
                                 boxShadow: 1,
                              }}
                           >
                              <Typography variant='subtitle2' gutterBottom>
                                 Select People
                              </Typography>

                              <Grid container spacing={2}>
                                 {Array.from(availableCategories).map((category) => {
                                    const peopleInCategory = availablePeople.filter(
                                       (p) => p.category === category
                                    );
                                    if (peopleInCategory.length === 0) return null;

                                    return (
                                       <Grid item xs={12} sm={6} md={4} key={category}>
                                          <Paper sx={{ p: 1, height: '100%' }}>
                                             <Typography
                                                variant='caption'
                                                color='text.secondary'
                                                sx={{ display: 'block', mb: 1, fontWeight: 'bold' }}
                                             >
                                                {category.charAt(0).toUpperCase() +
                                                   category.slice(1)}{' '}
                                                ({peopleInCategory.length})
                                             </Typography>

                                             <Stack spacing={0.5}>
                                                {peopleInCategory.map((person) => (
                                                   <Box
                                                      key={person.id}
                                                      sx={{
                                                         display: 'flex',
                                                         alignItems: 'center',
                                                         mb: 1,
                                                      }}
                                                   >
                                                      <Chip
                                                         label={
                                                            <Box
                                                               sx={{
                                                                  display: 'flex',
                                                                  alignItems: 'center',
                                                               }}
                                                            >
                                                               {person.photoURL ? (
                                                                  <Avatar
                                                                     src={person.photoURL}
                                                                     sx={{
                                                                        width: 24,
                                                                        height: 24,
                                                                        mr: 1,
                                                                     }}
                                                                  />
                                                               ) : (
                                                                  <Avatar
                                                                     sx={{
                                                                        width: 24,
                                                                        height: 24,
                                                                        mr: 1,
                                                                     }}
                                                                  >
                                                                     {person.name.charAt(0)}
                                                                  </Avatar>
                                                               )}
                                                               <Typography
                                                                  variant='body2'
                                                                  sx={{ fontWeight: 'medium' }}
                                                               >
                                                                  {person.name}
                                                               </Typography>
                                                            </Box>
                                                         }
                                                         color={
                                                            filters.peopleIds.has(person.id)
                                                               ? 'primary'
                                                               : 'default'
                                                         }
                                                         onClick={() =>
                                                            handleFilterChange(
                                                               'peopleIds',
                                                               person.id
                                                            )
                                                         }
                                                         clickable
                                                         sx={{
                                                            width: '100%',
                                                            justifyContent: 'flex-start',
                                                            pl: 0.5,
                                                            '& .MuiChip-label': {
                                                               paddingLeft: 0,
                                                            },
                                                         }}
                                                      />
                                                   </Box>
                                                ))}
                                             </Stack>
                                          </Paper>
                                       </Grid>
                                    );
                                 })}
                              </Grid>
                           </Box>
                        )}

                        {/* Summary of people filter selection */}
                        {(filters.peopleIds.size > 0 ||
                           filters.peopleCategories.size > 0 ||
                           inviteStatusFilter.size > 0) && (
                           <Box sx={{ mt: 2, p: 1.5, bgcolor: 'primary.50', borderRadius: 1 }}>
                              <Typography variant='body2' color='primary.dark'>
                                 Filtering by{' '}
                                 {[
                                    filters.peopleIds.size > 0
                                       ? `${filters.peopleIds.size} specific people`
                                       : null,
                                    filters.peopleCategories.size > 0
                                       ? `${filters.peopleCategories.size} relationship types`
                                       : null,
                                    inviteStatusFilter.size > 0
                                       ? `${inviteStatusFilter.size} invite statuses`
                                       : null,
                                 ]
                                    .filter(Boolean)
                                    .join(' and ')}
                              </Typography>
                           </Box>
                        )}
                     </>
                  )}
               </Box>
            }

            {/* Filter Stats */}
            <Box
               sx={{
                  mt: 2,
                  pt: 2,
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  borderTop: '1px dashed rgba(0,0,0,0.12)',
               }}
            >
               <Typography variant='body2' color='text.secondary'>
                  Showing {filteredTrips.length} trips and {filteredEvents.length} events
               </Typography>
               <Typography variant='body2' color='primary'>
                  {Object.values(filters).some(
                     (val) =>
                        (val instanceof Set && val.size > 0) ||
                        (Array.isArray(val) && val.some((v) => v !== null)) ||
                        (typeof val === 'string' && val.length > 0)
                  )
                     ? 'Filters active'
                     : 'No filters applied'}
               </Typography>
            </Box>
         </Paper>
      );
   };

   // Loading state
   if (loading) {
      return (
         <Paper
            sx={{
               p: 8,
               display: 'flex',
               flexDirection: 'column',
               alignItems: 'center',
               justifyContent: 'center',
               borderRadius: 2,
            }}
         >
            <CircularProgress size={60} thickness={4} sx={{ mb: 3 }} />
            <Typography variant='h6' color='text.secondary'>
               Loading your trips...
            </Typography>
         </Paper>
      );
   }

   // Error state
   if (error) {
      return (
         <Paper
            sx={{
               p: 8,
               display: 'flex',
               flexDirection: 'column',
               alignItems: 'center',
               justifyContent: 'center',
               borderRadius: 2,
            }}
         >
            <Alert severity='error' sx={{ width: '100%', mb: 2 }}>
               <AlertTitle>Error</AlertTitle>
               {error}
            </Alert>
            <Button variant='contained' onClick={fetchTrips}>
               Retry
            </Button>
         </Paper>
      );
   }

   // Empty state
   if (trips.length === 0) {
      return (
         <Paper
            sx={{
               p: 8,
               display: 'flex',
               flexDirection: 'column',
               alignItems: 'center',
               justifyContent: 'center',
               borderRadius: 2,
               backgroundColor: 'rgba(240, 247, 255, 0.7)',
               border: '1px dashed rgba(25, 118, 210, 0.5)',
               marginTop: 5,
               marginLeft: 5,
               marginRight: 5,
            }}
         >
            <img
               src='/images/empty-trips.svg'
               alt='No trips'
               style={{ width: '180px', height: 'auto', marginBottom: '24px', opacity: 0.8 }}
               onError={(e) => {
                  e.currentTarget.style.display = 'none';
               }}
            />
            <Typography variant='h5' gutterBottom sx={{ fontWeight: 500, color: 'primary.dark' }}>
               No trips yet
            </Typography>
            <Typography
               variant='body1'
               color='text.secondary'
               align='center'
               paragraph
               sx={{ maxWidth: '500px', mb: 3 }}
            >
               Start by adding your past adventures or planning your next trip.
            </Typography>
            {/* <Box sx={{ mt: 3, display: 'flex', justifyContent: 'center', gap: 2 }}>
               <Button
                  variant='contained'
                  color='primary'
                  startIcon={<AddIcon />}
                  onClick={() => setHistoricalFormOpen(true)}
                  size='large'
               >
                  Add Your First Trip
               </Button>
               <Button variant='outlined' component={Link} to='/trip-planner' size='large'>
                  Plan New Trip
               </Button>
            </Box> */}
         </Paper>
      );
   }

   // Improve the historicalTrips generation with better debugging
   const historicalTrips = trips.map((trip) => {
      if (!trip.startDate || !trip.endDate) {
         console.warn('Trip is missing start or end date:', trip);
      }

      // Ensure we have the correct primaryDestination format
      let primaryDestination = trip.primaryDestination;

      // If primaryDestination is missing but we have destination string, create one
      if (!primaryDestination && trip.destination) {
         primaryDestination = {
            name: trip.destination,
            coordinates: trip.coordinates
               ? [trip.coordinates.longitude, trip.coordinates.latitude]
               : undefined,
         };
      }

      const historicalTrip = {
         id: trip.id,
         title: trip.title,
         description: trip.description,
         startDate: trip.startDate,
         endDate: trip.endDate,
         primaryDestination,
         coverPhotoUrl: trip.coverPhotoUrl,
         coverImageUrl: trip.coverImageUrl,
         photoCount: trip.photoCount || 0,
         collaborators: trip.collaborators || [],
         commentCount: trip.commentCount || 0,
         isHistorical: trip.isHistorical || false,
         ownerCategory: trip.ownerCategory || inferCategory(trip.ownerName || '') || 'others',
      };

      return historicalTrip;
   });

   // Add a log statement before the return
   if (tabValue === 'historical') {
   }

   return (
      <Container
         maxWidth='xl'
         sx={{
            py: isMobile ? 2 : 4,
            px: isMobile ? 0.5 : 2,
            minHeight: 'calc(100vh - 150px)',
            maxWidth: isMobile ? '100%' : 'xl',
            margin: '0 auto',
         }}
      >
         <Box 
            sx={{ 
               mb: isMobile ? 2 : 4,
               px: isMobile ? 2 : 0,
               textAlign: isMobile ? 'center' : 'left',
            }}
         >
            <Typography
               variant='h4'
               gutterBottom
               sx={{
                  fontWeight: 'bold',
                  color: 'primary.main',
                  fontSize: isMobile ? fontSize.h4 : fontSize.h3,
                  textAlign: isMobile ? 'center' : 'left',
               }}
            >
               Timeline
            </Typography>

            {/* Introduction and guidance text */}
            <Paper
               elevation={0}
               sx={{
                  p: isMobile ? 2 : 3,
                  mb: isMobile ? 2 : 4,
                  mx: isMobile ? 1 : 0,
                  bgcolor: 'rgba(144, 202, 249, 0.08)',
                  border: '1px solid rgba(144, 202, 249, 0.2)',
                  borderRadius: isMobile ? 1 : 2,
               }}
            >
               <Typography
                  variant='h6'
                  color='primary'
                  gutterBottom
                  sx={{
                     fontSize: isMobile ? fontSize.h6 : fontSize.h6,
                     textAlign: isMobile ? 'center' : 'left',
                  }}
               >
                  {currentUser?.displayName
                     ? `${currentUser.displayName}'s Travel Journey`
                     : 'Your Travel Journey'}
               </Typography>
               <Typography
                  variant='body1'
                  paragraph
                  sx={{
                     fontSize: isMobile ? fontSize.body2 : fontSize.body1,
                     textAlign: isMobile ? 'center' : 'left',
                  }}
               >
                  Your timeline captures all your travel experiences in one chronological view. Here
                  you can see your past trips, upcoming events, and special moments organized by
                  date.
               </Typography>
               <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, mt: 2 }}>
                  <Typography
                     variant='body1'
                     sx={{
                        display: 'flex',
                        alignItems: 'center',
                        fontSize: isMobile ? fontSize.body2 : fontSize.body1,
                        flexDirection: isMobile ? 'column' : 'row',
                        textAlign: isMobile ? 'center' : 'left',
                     }}
                  >
                     <CalendarMonthIcon
                        color='primary'
                        sx={{ mr: isMobile ? 0 : 1, mb: isMobile ? 0.5 : 0 }}
                     />
                     <strong>Trips and Events:</strong> See your travels and gatherings in one
                     chronological stream
                  </Typography>
                  <Typography
                     variant='body1'
                     sx={{
                        display: 'flex',
                        alignItems: 'center',
                        fontSize: isMobile ? fontSize.body2 : fontSize.body1,
                        flexDirection: isMobile ? 'column' : 'row',
                        textAlign: isMobile ? 'center' : 'left',
                     }}
                  >
                     <PhotoIcon
                        color='secondary'
                        sx={{ mr: isMobile ? 0 : 1, mb: isMobile ? 0.5 : 0 }}
                     />
                     <strong>Photos and Memories:</strong> View photos and notes attached to your
                     experiences
                  </Typography>
                  <Typography
                     variant='body1'
                     sx={{
                        display: 'flex',
                        alignItems: 'center',
                        fontSize: isMobile ? fontSize.body2 : fontSize.body1,
                        flexDirection: isMobile ? 'column' : 'row',
                        textAlign: isMobile ? 'center' : 'left',
                     }}
                  >
                     <PeopleIcon
                        color='success'
                        sx={{ mr: isMobile ? 0 : 1, mb: isMobile ? 0.5 : 0 }}
                     />
                     <strong>Shared Experiences:</strong> See trips and events you've shared with
                     friends and family
                  </Typography>
               </Box>
               <Box
                  sx={{
                     mt: isMobile ? 2 : 3,
                     display: 'flex',
                     gap: isMobile ? 1 : 2,
                     flexDirection: isMobile ? 'column' : 'row',
                     alignItems: isMobile ? 'stretch' : 'center',
                  }}
               >
                  <Button
                     variant='contained'
                     color='primary'
                     startIcon={<AddIcon />}
                     onClick={() => {
                        if (checkSubscriptionStatus()) {
                           setHistoricalFormOpen(true);
                        }
                     }}
                     size={isMobile ? 'large' : 'medium'}
                     sx={{
                        minHeight: isMobile ? '48px' : '40px',
                        px: isMobile ? 3 : 2,
                        fontSize: isMobile ? fontSize.body1 : 'inherit',
                        width: isMobile ? '100%' : 'auto',
                     }}
                  >
                     Add Past Trip
                  </Button>
                  <Button
                     variant='outlined'
                     color='secondary'
                     startIcon={<EventIcon />}
                     onClick={() => {
                        if (checkSubscriptionStatus()) {
                           setEventFormOpen(true);
                        }
                     }}
                     size={isMobile ? 'large' : 'medium'}
                     sx={{
                        minHeight: isMobile ? '48px' : '40px',
                        px: isMobile ? 3 : 2,
                        fontSize: isMobile ? fontSize.body1 : 'inherit',
                        width: isMobile ? '100%' : 'auto',
                     }}
                  >
                     Create Event
                  </Button>
               </Box>
            </Paper>
         </Box>

         {/* Show error alert if there's an error */}
         {error && (
            <Alert severity='error' sx={{ mb: 3, mx: isMobile ? 1 : 0 }}>
               {error}
            </Alert>
         )}

         {/* Show missing indexes alert if needed */}
         {missingIndexes.length > 0 && (
            <Alert
               severity='warning'
               sx={{ mb: 3, mx: isMobile ? 1 : 0 }}
               action={
                  <Button
                     color='inherit'
                     size='small'
                     onClick={() => {
                        // Open the first missing index in a new tab
                        if (missingIndexes.length > 0) {
                           window.open(missingIndexes[0], '_blank');
                        }
                     }}
                  >
                     Create Index
                  </Button>
               }
            >
               <AlertTitle>Missing Firebase Indexes</AlertTitle>
               <Typography variant='body2' gutterBottom>
                  Some queries require indexes to be created in Firebase. Click "Create Index" to
                  create the required indexes one by one.
               </Typography>
               <Typography variant='caption' color='text.secondary'>
                  {missingIndexes.length} missing{' '}
                  {missingIndexes.length === 1 ? 'index' : 'indexes'}
               </Typography>
            </Alert>
         )}

         {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
               <CircularProgress />
            </Box>
         ) : error ? (
            <></>
         ) : trips.length === 0 && events.length === 0 ? (
            <Paper
               sx={{
                  p: isMobile ? 3 : 5,
                  mx: isMobile ? 1 : 0,
                  textAlign: 'center',
                  borderRadius: isMobile ? 1 : 2,
                  backgroundImage: 'linear-gradient(to right bottom, #f5f7fa, #f8f9fb)',
                  boxShadow: 2,
               }}
            >
               <Typography
                  variant='h5'
                  gutterBottom
                  sx={{
                     fontSize: isMobile ? fontSize.h5 : fontSize.h4,
                  }}
               >
                  Start Your Travel Journey
               </Typography>
               <Typography
                  variant='body1'
                  color='text.secondary'
                  paragraph
                  sx={{
                     fontSize: isMobile ? fontSize.body2 : fontSize.body1,
                  }}
               >
                  Create your first trip or event to start building your personal travel timeline.
               </Typography>
               <Box
                  sx={{
                     mt: isMobile ? 2 : 3,
                     display: 'flex',
                     justifyContent: 'center',
                     gap: isMobile ? 1 : 2,
                     flexDirection: isMobile ? 'column' : 'row',
                     alignItems: 'stretch',
                  }}
               >
                  <Button
                     variant='contained'
                     color='primary'
                     startIcon={<AddIcon />}
                     onClick={() => setHistoricalFormOpen(true)}
                     size={isMobile ? 'large' : 'large'}
                     sx={{
                        minHeight: isMobile ? '48px' : '40px',
                        px: isMobile ? 3 : 2,
                        fontSize: isMobile ? fontSize.body1 : 'inherit',
                        width: isMobile ? '100%' : 'auto',
                     }}
                  >
                     Add Past Trip
                  </Button>
                  <Button
                     variant='outlined'
                     component={Link}
                     to='/trip-planner'
                     size={isMobile ? 'large' : 'large'}
                     sx={{
                        minHeight: isMobile ? '48px' : '40px',
                        px: isMobile ? 3 : 2,
                        fontSize: isMobile ? fontSize.body1 : 'inherit',
                        width: isMobile ? '100%' : 'auto',
                     }}
                  >
                     Plan New Trip
                  </Button>
               </Box>
            </Paper>
         ) : (
            <>
               {/* Timeline Section */}
               <Box
                  sx={{
                     display: 'flex',
                     justifyContent: 'space-between',
                     alignItems: 'center',
                     mb: isMobile ? 2 : 3,
                     flexDirection: isMobile ? 'column' : 'row',
                     gap: isMobile ? 2 : 0,
                     px: isMobile ? 2 : 0,
                  }}
               >
                  <Typography
                     variant='h5'
                     sx={{
                        fontWeight: 'medium',
                        fontSize: isMobile ? fontSize.h5 : fontSize.h4,
                        textAlign: isMobile ? 'center' : 'left',
                     }}
                  >
                     Your Timeline ({trips.length > 0 && events.length > 0 ? `${trips.length + events.length}` : trips.length > 0 ? `${trips.length} Trips` : events.length > 0 ? `${events.length} Events` : '0'})
                  </Typography>
                  {/* <Box sx={{ display: 'flex', gap: 2 }}>
                     <Button 
                        variant="contained"
                        startIcon={<AddIcon />}
                        onClick={() => setHistoricalFormOpen(true)}
                        sx={{ 
                           borderRadius: '8px',
                           boxShadow: 2,
                           px: 3
                        }}
                     >
                        Add Trip
                     </Button>
                     <Button
                        variant="contained"
                        color="secondary"
                        startIcon={<EventIcon />}
                        onClick={() => setEventFormOpen(true)}
                        sx={{
                           borderRadius: '8px',
                           boxShadow: 2,
                           px: 3
                        }}
                     >
                        Add Event
                     </Button>
                  </Box> */}
               </Box>

               {/* Main Content Section */}
               {!selectedTrip ? (
                  // Trip Timeline View
                  <Box sx={{ mt: 3 }}>
                     <Box
                        sx={{
                           display: 'flex',
                           justifyContent: 'space-between',
                           alignItems: isMobile ? 'stretch' : 'center',
                           mb: isMobile ? 2 : 3,
                           flexDirection: isMobile ? 'column' : 'row',
                           gap: isMobile ? 2 : 0,
                           px: isMobile ? 2 : 0,
                        }}
                     >
                        <Button
                           variant='outlined'
                           startIcon={<FilterListIcon />}
                           onClick={() => setFiltersVisible(!filtersVisible)}
                           color={filtersVisible ? 'primary' : 'inherit'}
                           size={isMobile ? 'large' : 'medium'}
                           sx={{
                              minHeight: isMobile ? '48px' : '40px',
                              px: isMobile ? 3 : 2,
                              fontSize: isMobile ? fontSize.body1 : 'inherit',
                              width: isMobile ? '100%' : 'auto',
                           }}
                        >
                           {filtersVisible ? 'Hide Filters' : 'Show Filters'}
                        </Button>
                        <FormControlLabel
                           control={
                              <Switch
                                 color='primary'
                                 checked={sortNewest}
                                 onChange={(e) => setSortNewest(e.target.checked)}
                              />
                           }
                           label='Show newest first'
                           sx={{
                              mr: isMobile ? 0 : 2,
                              fontSize: isMobile ? fontSize.body2 : 'inherit',
                              textAlign: isMobile ? 'center' : 'left',
                           }}
                        />
                     </Box>

                     {/* Add filters UI here */}
                     {filtersVisible && renderFilters()}

                     {/* Timeline Layout */}
                     <Box
                        sx={{
                           position: 'relative',
                           ml: isMobile ? 1 : 4,
                           pl: isMobile ? 1.5 : 4,
                           borderLeft: '2px dashed rgba(25, 118, 210, 0.6)',
                           py: isMobile ? 1 : 2,
                           mr: isMobile ? 1 : 0,
                        }}
                     >
                        {renderYearlyTimeline()}
                     </Box>
                  </Box>
               ) : tabValue === 'photo' ? (
                  // Photo Gallery View
                  <Box sx={{ mt: isMobile ? 1 : 2 }}>
                     <Paper
                        sx={{
                           p: isMobile ? 1.5 : 2,
                           mb: isMobile ? 2 : 3,
                           borderRadius: isMobile ? 1 : 2,
                        }}
                     >
                        <Box
                           sx={{
                              display: 'flex',
                              justifyContent: 'space-between',
                              alignItems: isMobile ? 'stretch' : 'center',
                              flexDirection: isMobile ? 'column' : 'row',
                              gap: isMobile ? 2 : 0,
                           }}
                        >
                           <Box sx={{ textAlign: isMobile ? 'center' : 'left' }}>
                              <Typography
                                 variant='h5'
                                 sx={{
                                    fontSize: isMobile ? fontSize.h5 : fontSize.h4,
                                 }}
                              >
                                 {selectedTrip.title}
                              </Typography>
                              <Typography
                                 variant='body2'
                                 color='text.secondary'
                                 sx={{
                                    fontSize: isMobile ? fontSize.body2 : fontSize.body1,
                                 }}
                              >
                                 {selectedTrip.destination || 'No location'} •{' '}
                                 {new Date(selectedTrip.startDate).toLocaleDateString()} -{' '}
                                 {new Date(selectedTrip.endDate).toLocaleDateString()}
                              </Typography>
                           </Box>

                           <Box
                              sx={{
                                 display: 'flex',
                                 gap: isMobile ? 1 : 2,
                                 flexDirection: isMobile ? 'column' : 'row',
                                 width: isMobile ? '100%' : 'auto',
                              }}
                           >
                              <Button
                                 variant='outlined'
                                 startIcon={<ArrowBackIcon />}
                                 onClick={() => {
                                    setSelectedTrip(null);
                                    setTabValue('historical');
                                 }}
                                 size={isMobile ? 'large' : 'small'}
                                 sx={{
                                    minHeight: isMobile ? '48px' : '40px',
                                    px: isMobile ? 3 : 2,
                                    fontSize: isMobile ? fontSize.body1 : 'inherit',
                                    width: isMobile ? '100%' : 'auto',
                                 }}
                              >
                                 Back
                              </Button>
                              <Button
                                 variant='contained'
                                 disableElevation
                                 startIcon={<AddIcon />}
                                 onClick={() => navigate(`/trip/${selectedTrip.id}?tab=2`)}
                                 size={isMobile ? 'large' : 'small'}
                                 sx={{
                                    minHeight: isMobile ? '48px' : '40px',
                                    px: isMobile ? 3 : 2,
                                    fontSize: isMobile ? fontSize.body1 : 'inherit',
                                    width: isMobile ? '100%' : 'auto',
                                 }}
                              >
                                 Add Photos
                              </Button>
                           </Box>
                        </Box>
                     </Paper>

                     <Grid container spacing={2}>
                        <Grid item xs={12} md={9}>
                           <Paper sx={{ borderRadius: 2, overflow: 'hidden' }}>
                              {photoLoading ? (
                                 <Box sx={{ display: 'flex', justifyContent: 'center', p: 6 }}>
                                    <CircularProgress />
                                 </Box>
                              ) : photos.length === 0 ? (
                                 <Box
                                    sx={{
                                       display: 'flex',
                                       flexDirection: 'column',
                                       alignItems: 'center',
                                       justifyContent: 'center',
                                       p: 6,
                                    }}
                                 >
                                    <PhotoIcon
                                       sx={{ fontSize: 60, color: 'text.disabled', mb: 2 }}
                                    />
                                    <Typography variant='h6' gutterBottom>
                                       No photos yet
                                    </Typography>
                                    <Typography
                                       variant='body2'
                                       color='text.secondary'
                                       paragraph
                                       align='center'
                                    >
                                       Add photos to document your journey and share memories.
                                    </Typography>
                                    <Button
                                       variant='contained'
                                       onClick={() => navigate(`/trip/${selectedTrip.id}?tab=2`)}
                                    >
                                       Upload Photos
                                    </Button>
                                 </Box>
                              ) : (
                                 <Box>
                                    <Box sx={{ p: 2, borderBottom: 1, borderColor: 'divider' }}>
                                       <Typography variant='subtitle1'>
                                          Photos ({photos.length})
                                       </Typography>
                                    </Box>
                                    <Grid container>
                                       {photos.map((photo, index) => (
                                          <Grid
                                             item
                                             xs={6}
                                             sm={4}
                                             md={3}
                                             lg={2}
                                             key={photo.id || index}
                                          >
                                             <Box
                                                sx={{
                                                   position: 'relative',
                                                   paddingTop: '100%',
                                                   cursor: 'pointer',
                                                   '&:hover': {
                                                      '& > .MuiBox-root': {
                                                         opacity: 1,
                                                      },
                                                   },
                                                }}
                                                onClick={() => {
                                                   openImageDialog(photos, index);
                                                }}
                                             >
                                                <img
                                                   src={photo.thumbnailUrl || photo.url}
                                                   alt={photo.title || 'Trip photo'}
                                                   style={{
                                                      position: 'absolute',
                                                      top: 0,
                                                      left: 0,
                                                      width: '100%',
                                                      height: '100%',
                                                      objectFit: 'cover',
                                                   }}
                                                   onError={(e) => {
                                                      console.error(
                                                         'Image load error for:',
                                                         photo.url
                                                      );
                                                      (e.target as HTMLImageElement).src =
                                                         'https://via.placeholder.com/300x300?text=Photo';
                                                   }}
                                                />
                                                <Box
                                                   sx={{
                                                      position: 'absolute',
                                                      top: 0,
                                                      left: 0,
                                                      right: 0,
                                                      bottom: 0,
                                                      backgroundColor: 'rgba(0, 0, 0, 0.4)',
                                                      display: 'flex',
                                                      alignItems: 'center',
                                                      justifyContent: 'center',
                                                      opacity: 0,
                                                      transition: 'opacity 0.2s',
                                                   }}
                                                >
                                                   <ZoomInIcon
                                                      sx={{ color: 'white', fontSize: 30 }}
                                                   />
                                                </Box>
                                             </Box>
                                          </Grid>
                                       ))}
                                    </Grid>
                                 </Box>
                              )}
                           </Paper>
                        </Grid>

                        {/* Chat Section */}
                        <Grid item xs={12} md={3}>
                           <Paper
                              sx={{
                                 borderRadius: 2,
                                 height: '100%',
                                 display: 'flex',
                                 flexDirection: 'column',
                              }}
                           >
                              <Box sx={{ p: 2, borderBottom: 1, borderColor: 'divider' }}>
                                 <Typography variant='subtitle1'>Trip Chat</Typography>
                              </Box>

                              <Box
                                 sx={{
                                    flexGrow: 1,
                                    display: 'flex',
                                    flexDirection: 'column',
                                    p: 2,
                                    height: '400px',
                                    overflow: 'auto',
                                 }}
                              >
                                 <Box
                                    sx={{
                                       flexGrow: 1,
                                       display: 'flex',
                                       flexDirection: 'column',
                                       justifyContent: 'center',
                                       alignItems: 'center',
                                    }}
                                 >
                                    <ChatIcon
                                       sx={{ fontSize: 40, color: 'text.disabled', mb: 2 }}
                                    />
                                    <Typography
                                       variant='body2'
                                       color='text.secondary'
                                       align='center'
                                    >
                                       Chat with your travel companions about this trip.
                                    </Typography>
                                 </Box>

                                 {/* Chat messages would go here */}
                              </Box>

                              <Box sx={{ p: 2, borderTop: 1, borderColor: 'divider' }}>
                                 <Box sx={{ display: 'flex', gap: 1 }}>
                                    <TextField
                                       fullWidth
                                       size='small'
                                       placeholder='Type a message...'
                                       variant='outlined'
                                    />
                                    <IconButton color='primary'>
                                       <SendIcon />
                                    </IconButton>
                                 </Box>
                              </Box>
                           </Paper>
                        </Grid>
                     </Grid>
                  </Box>
               ) : (
                  // Trip Timeline View
                  <Box sx={{ mt: 2 }}>
                     <Paper sx={{ p: 2, mb: 3, borderRadius: 2 }}>
                        <Box
                           sx={{
                              display: 'flex',
                              justifyContent: 'space-between',
                              alignItems: 'center',
                           }}
                        >
                           <Box>
                              <Typography variant='h5'>{selectedTrip.title}</Typography>
                              <Typography variant='body2' color='text.secondary'>
                                 {selectedTrip.destination || 'No location'} •{' '}
                                 {new Date(selectedTrip.startDate).toLocaleDateString()} -{' '}
                                 {new Date(selectedTrip.endDate).toLocaleDateString()}
                              </Typography>
                           </Box>

                           <Button
                              variant='outlined'
                              startIcon={<ArrowBackIcon />}
                              onClick={() => {
                                 setSelectedTrip(null);
                                 setTabValue('historical');
                              }}
                           >
                              Back to Trips
                           </Button>
                        </Box>
                     </Paper>

                     <Grid container spacing={2}>
                        <Grid item xs={12} md={9}>
                           <Paper sx={{ borderRadius: 2, p: 0, overflow: 'hidden' }}>
                              <TimelineContainer
                                 canUserAccess={canUserAccess}
                                 tripName={selectedTrip.title}
                                 startDate={selectedTrip.startDate}
                                 endDate={selectedTrip.endDate}
                                 activities={activities}
                                 accommodations={accommodations}
                                 transportations={transportations}
                                 events={events}
                                 onEditActivity={handleEditActivity}
                                 onDeleteActivity={handleDeleteActivity}
                                 onAddComment={handleAddComment}
                                 currentUser={
                                    currentUser
                                       ? {
                                            id: currentUser.uid,
                                            name: currentUser.displayName || 'User',
                                            avatar: currentUser.photoURL || undefined,
                                         }
                                       : undefined
                                 }
                                 collaborators={(selectedTrip.collaborators || []).map(
                                    (c: any) => ({
                                       ...c,
                                       role: (c.st || 'viewer') as 'owner' | 'editor' | 'viewer',
                                    })
                                 )}
                              />
                           </Paper>
                        </Grid>

                        {/* Chat Section */}
                        <Grid item xs={12} md={3}>
                           <Paper
                              sx={{
                                 borderRadius: 2,
                                 height: '100%',
                                 display: 'flex',
                                 flexDirection: 'column',
                              }}
                           >
                              <Box sx={{ p: 2, borderBottom: 1, borderColor: 'divider' }}>
                                 <Typography variant='subtitle1'>Trip Chat</Typography>
                              </Box>

                              <Box
                                 sx={{
                                    flexGrow: 1,
                                    display: 'flex',
                                    flexDirection: 'column',
                                    p: 2,
                                    height: '400px',
                                    overflow: 'auto',
                                 }}
                              >
                                 <Box
                                    sx={{
                                       flexGrow: 1,
                                       display: 'flex',
                                       flexDirection: 'column',
                                       justifyContent: 'center',
                                       alignItems: 'center',
                                    }}
                                 >
                                    <ChatIcon
                                       sx={{ fontSize: 40, color: 'text.disabled', mb: 2 }}
                                    />
                                    <Typography
                                       variant='body2'
                                       color='text.secondary'
                                       align='center'
                                    >
                                       Chat with your travel companions about this trip.
                                    </Typography>
                                 </Box>

                                 {/* Chat messages would go here */}
                              </Box>

                              <Box sx={{ p: 2, borderTop: 1, borderColor: 'divider' }}>
                                 <Box sx={{ display: 'flex', gap: 1 }}>
                                    <TextField
                                       fullWidth
                                       size='small'
                                       placeholder='Type a message...'
                                       variant='outlined'
                                    />
                                    <IconButton color='primary'>
                                       <SendIcon />
                                    </IconButton>
                                 </Box>
                              </Box>
                           </Paper>
                        </Grid>
                     </Grid>
                  </Box>
               )}
            </>
         )}

         {/* Gallery Dialog */}
         {imageDialogOpen && (
            <TimelineImageGallery
               images={currentImages}
               initialIndex={currentImageIndex}
               title={selectedTrip ? `${selectedTrip.title} Photos` : 'Photos'}
               onClose={closeImageDialog}
               isEditable={false}
            />
         )}

         {/* Historical Trip Form */}
         <HistoricalTripForm
            open={historicalFormOpen}
            onClose={() => setHistoricalFormOpen(false)}
            onSuccess={handleTripCreated}
         />

         {/* Event Creation Dialog */}
         <EventCreationDialog
            open={eventFormOpen}
            onClose={() => setEventFormOpen(false)}
            onSuccess={handleEventCreated}
         />

         {/* Trip/Event Modal */}
         <TripEventModal
            open={modalOpen}
            onClose={() => {
               setModalOpen(false);
               setModalData(null);
            }}
            data={modalData}
         />
      </Container>
   );
};

export default TimelineView;
